import { Bot, CheckCircle2, ChevronDown, ChevronUp, FileText, Headphones, Loader2, MessageSquare, Mic, RotateCcw, Send, Shapes, Video, Volume2, VolumeX, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ApiService } from '../../services/api';

type TutorRole = 'user' | 'assistant';
type TutorToolName = 'show_pdf_on_canvas' | 'open_lesson_video' | 'open_simulation' | 'open_practice_test' | 'move_to_next_gain';

interface TutorMessage {
  id?: string;
  role: TutorRole;
  content: string;
  transient?: boolean;
}

interface AITutorToolCall {
  id?: string;
  name: TutorToolName;
  arguments: Record<string, any>;
}

interface TutorQuestion {
  id: string | number;
  text?: string;
  options?: Record<string, string> | string[] | Array<{ text?: string; value?: string; label?: string }>;
  correctAnswer?: string;
  mediaUrl?: string;
  solutionVideoUrl?: string;
}

interface PracticeTestResultSummary {
  total: number;
  answered: number;
  correct: number;
  wrong: number;
  blank: number;
  wrongQuestions: Array<{
    index: number;
    text: string;
    selectedAnswer?: string;
    selectedAnswerText?: string;
    correctAnswer?: string;
    correctAnswerText?: string;
  }>;
}

type AITeacherLiveSession = Awaited<ReturnType<typeof ApiService.createAITeacherLiveSession>>;

interface AITeacherLiveConnection {
  session: AITeacherLiveSession;
  socket: WebSocket;
  contextKey: string;
}

type PdfPageImage = {
  id?: string | number;
  pageNumber: number;
  imageUrl: string;
};

type ActiveMaterial =
  | { type: "empty" }
  | {
    type: "pdf";
    pdfUrl: string;
    pageNumber: number;
    title?: string;
    totalPages?: number;
    pageImages?: PdfPageImage[];
  }
  | { type: "video"; videoId: string; videoUrl: string; title: string; startTime: string; endTime: string; transcriptExcerpt?: string }
  | { type: "simulation"; topicId: string }
  | { type: "test-loading"; gainId: string }
  | { type: "test-error"; gainId: string; message: string }
  | { type: "test"; gainId: string; questionId?: string; questions: TutorQuestion[]; source?: string };

interface AITutorProps {
  isOpen: boolean;
  context: Record<string, any>;
  onClose: () => void;
  sidebarExpanded?: boolean;
  onShowPdfOnCanvas?: (payload: { pdf_url: string; page_number: number }) => void;
  onOpenSimulation?: (payload: { topic_id: string }) => void;
}

const AI_TUTOR_TYPING_SETTINGS = {
  audioDurationMultiplier: 0.95,
  fallbackMsPerCharacter: 45,
  minDurationMs: 1200,
  maxDurationMs: 90000,
  syncedStartDelayMs: 140,
  minCharacterDelayMs: 18,
  maxCharacterDelayMs: 55,
  spaceDelayMs: 8,
  pausesMs: {
    comma: 120,
    clause: 180,
    sentence: 280,
    paragraph: 380,
    turnEnd: 220,
  },
};
const AI_TUTOR_LIVE_FIRST_DATA_TIMEOUT_MS = 15000;
const AI_TUTOR_LIVE_TURN_COMPLETE_GRACE_MS = 900;
const AI_TUTOR_SPEECH_STREAM_TEXT_FALLBACK_MS = 2200;
const AI_TUTOR_RECORDING_MAX_MS = 22000;
const AI_TUTOR_RECORDING_SILENCE_MS = 1100;
const AI_TUTOR_RECORDING_SPEECH_RMS = 0.035;
const AI_TUTOR_VOICE_LEVEL_IDLE = 0.08;

const TEXTUAL_TOOL_CALL_NAMES: TutorToolName[] = ["open_lesson_video", "show_pdf_on_canvas", "open_simulation", "open_practice_test"];
const TEXTUAL_TOOL_CALL_PATTERN = new RegExp(`(^|\\n)\\s*(${TEXTUAL_TOOL_CALL_NAMES.join("|")})\\s*\\(([^)]*)\\)\\s*`, "g");

const splitToolCallArguments = (argsText = "") => {
  const args: string[] = [];
  let current = "";
  let quote = "";
  let escaped = false;

  for (const char of argsText) {
    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }

    if (char === "\\") {
      current += char;
      escaped = true;
      continue;
    }

    if (quote) {
      current += char;
      if (char === quote) quote = "";
      continue;
    }

    if (char === '"' || char === "'") {
      current += char;
      quote = char;
      continue;
    }

    if (char === ",") {
      if (current.trim()) args.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  if (current.trim()) args.push(current.trim());
  return args;
};

const parseToolCallArgumentValue = (value = "") => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
    return trimmed.slice(1, -1).replace(/\\(['"\\])/g, "$1");
  }

  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  if (/^(true|false)$/i.test(trimmed)) return /^true$/i.test(trimmed);

  return trimmed;
};

const parseTextualToolCallArguments = (argsText = "") =>
  splitToolCallArguments(argsText).reduce<Record<string, any>>((acc, item) => {
    const match = item.match(/^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*([\s\S]*)$/);
    if (!match) return acc;

    acc[match[1]] = parseToolCallArgumentValue(match[2]);
    return acc;
  }, {});

const extractTextualToolCallsFromMessage = (message = "") => {
  const toolCalls: AITutorToolCall[] = [];
  const cleanedMessage = String(message || "")
    .replace(TEXTUAL_TOOL_CALL_PATTERN, (fullMatch, linePrefix, name, argsText) => {
      const toolName = name as TutorToolName;
      toolCalls.push({
        id: `textual-tool-${toolCalls.length}`,
        name: toolName,
        arguments: parseTextualToolCallArguments(argsText),
      });

      return linePrefix || "";
    })
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return { message: cleanedMessage, toolCalls };
};

const getTypingPauseAfterChar = (text: string, index: number) => {
  const char = text[index] || "";
  const nextChar = text[index + 1] || "";

  if (char === "\n") return AI_TUTOR_TYPING_SETTINGS.pausesMs.paragraph;
  if (/[.!?]/.test(char)) {
    return nextChar === "\n" ? AI_TUTOR_TYPING_SETTINGS.pausesMs.paragraph : AI_TUTOR_TYPING_SETTINGS.pausesMs.sentence;
  }
  if (/[;:]/.test(char)) return AI_TUTOR_TYPING_SETTINGS.pausesMs.clause;
  if (char === ",") return AI_TUTOR_TYPING_SETTINGS.pausesMs.comma;

  return 0;
};

const streamAssistantText = (
  fullText: string,
  onUpdate: (partialText: string) => void,
  onDone?: () => void,
  durationMs?: number,
) => {
  const text = fullText || "";
  const timerIds = new Set<number>();
  let isStopped = false;
  let didFinish = false;
  const clearTimers = () => {
    timerIds.forEach((timerId) => window.clearTimeout(timerId));
    timerIds.clear();
  };
  const finish = () => {
    if (didFinish) return;
    didFinish = true;
    isStopped = true;
    clearTimers();
    onDone?.();
  };

  if (!text) {
    finish();

    return { flush: finish, cancel: finish };
  };

  const characters = Array.from(text);
  const targetDuration = durationMs
    ? Math.min(
      AI_TUTOR_TYPING_SETTINGS.maxDurationMs,
      Math.max(AI_TUTOR_TYPING_SETTINGS.minDurationMs, durationMs * AI_TUTOR_TYPING_SETTINGS.audioDurationMultiplier),
    )
    : estimateSpeechDurationMs(text);

  const totalPauseMs = characters.reduce((sum, _, index) => sum + getTypingPauseAfterChar(text, index), 0);
  const typingDuration = Math.max(900, targetDuration - totalPauseMs);
  const baseCharacterDelayMs = Math.min(
    AI_TUTOR_TYPING_SETTINGS.maxCharacterDelayMs,
    Math.max(AI_TUTOR_TYPING_SETTINGS.minCharacterDelayMs, typingDuration / Math.max(1, characters.length)),
  );
  let currentIndex = 0;

  const tick = () => {
    if (isStopped) return;
    if (currentIndex >= characters.length) {
      const doneTimer = window.setTimeout(finish, AI_TUTOR_TYPING_SETTINGS.pausesMs.turnEnd);
      timerIds.add(doneTimer);
      return;
    }

    currentIndex += 1;
    onUpdate(characters.slice(0, currentIndex).join(""));

    const currentChar = characters[currentIndex - 1] || "";
    const pauseMs = getTypingPauseAfterChar(text, currentIndex - 1);
    const nextDelayMs = /\s/.test(currentChar)
      ? AI_TUTOR_TYPING_SETTINGS.spaceDelayMs
      : baseCharacterDelayMs + pauseMs;

    const tickTimer = window.setTimeout(() => {
      timerIds.delete(tickTimer);
      tick();
    }, nextDelayMs);
    timerIds.add(tickTimer);
  };

  tick();

  return {
    flush: () => {
      if (didFinish) return;
      currentIndex = characters.length;
      onUpdate(text);
      finish();
    },
    cancel: finish,
  };
};

const estimateSpeechDurationMs = (text: string) => {
  const normalizedLength = text.replace(/\s+/g, " ").trim().length;
  return Math.min(
    AI_TUTOR_TYPING_SETTINGS.maxDurationMs,
    Math.max(AI_TUTOR_TYPING_SETTINGS.minDurationMs, normalizedLength * AI_TUTOR_TYPING_SETTINGS.fallbackMsPerCharacter),
  );
};

const base64ToUint8Array = (base64: string) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const getSampleRateFromMimeType = (mimeType = "") => {
  const match = String(mimeType || "").match(/rate=(\d+)/i);
  return Number(match?.[1]) || 24000;
};

const appendLiveTextFragment = (current: string, fragment: string) => {
  const cleanFragment = String(fragment || "").replace(/\s+/g, " ").trim();
  const cleanCurrent = String(current || "").replace(/\s+/g, " ").trim();
  if (!cleanFragment) return cleanCurrent;
  if (!cleanCurrent) return cleanFragment;
  if (cleanCurrent.endsWith(cleanFragment)) return cleanCurrent;
  if (cleanFragment.startsWith(cleanCurrent)) return cleanFragment;

  const needsSpace = !/\s$/.test(cleanCurrent) && !/^[,.;:!?]/.test(cleanFragment);
  return `${cleanCurrent}${needsSpace ? " " : ""}${cleanFragment}`;
};

const isLikelyIncompleteAssistantText = (text = "") => {
  const cleaned = String(text || "").replace(/\s+/g, " ").trim();
  if (!cleaned) return true;
  if (/[.!?…]$/.test(cleaned)) return false;
  if (/[,;:]$/.test(cleaned)) return true;
  if (/\b(ve|veya|ya da|ile|ama|fakat|çünkü|ki|sence|peki|örneğin|mesela)$/i.test(cleaned)) return true;

  return false;
};

const isInternalMetaAssistantText = (text = "") => {
  const cleaned = String(text || "").replace(/\s+/g, " ").trim();
  if (!cleaned) return false;
  const lower = cleaned.toLocaleLowerCase("en-US");
  const englishMetaHitCount = [
    "tutoring",
    "session",
    "focusing",
    "aiming",
    "initial",
    "target",
    "missing gain",
    "module",
    "according to",
    "plan",
  ].filter((pattern) => lower.includes(pattern)).length;

  return (
    englishMetaHitCount >= 2 ||
    /^\*\*[^*]*(?:focusing|tutoring|session|teacher response)[^*]*\*\*/i.test(cleaned) ||
    /\binitiating tutoring session\b/i.test(cleaned) ||
    /\bfocusing on .*tutoring\b/i.test(cleaned) ||
    /\bi'?m aiming\b/i.test(cleaned) ||
    /\bi'?m starting (?:a )?new tutoring session\b/i.test(cleaned) ||
    /\bmy initial focus\b/i.test(cleaned) ||
    /\bcentral target\b/i.test(cleaned) ||
    /\bmissing gain\b/i.test(cleaned) ||
    /\baccording to the plan\b/i.test(cleaned) ||
    /\baktif ekran bağlamı\b/i.test(cleaned) ||
    /\bsystem instruction\b/i.test(cleaned)
  );
};

const getSafeAssistantMessage = (message: string, fallback: string) =>
  isInternalMetaAssistantText(message) ? fallback : message;

const parseMentionedPdfPageNumber = (text = "") => {
  const normalized = String(text || "")
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ş/g, "s")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g");

  const numericPageMatch =
    normalized.match(/(?:sayfa(?:sinda|sinde|da|de|yi|i|ya|ye)?\s*(\d{1,3})|(\d{1,3})\s*[.)]?\s*sayfa(?:sinda|sinde|da|de|yi|i|ya|ye)?)/i);
  const numericPage = Number(numericPageMatch?.[1] || numericPageMatch?.[2] || 0);
  if (numericPage > 0) return numericPage;

  const ordinalPages: Record<string, number> = {
    ilk: 1,
    birinci: 1,
    ikinci: 2,
    ucuncu: 3,
    dorduncu: 4,
    besinci: 5,
    altinci: 6,
    yedinci: 7,
    sekizinci: 8,
    dokuzuncu: 9,
    onuncu: 10,
  };

  const ordinalMatch = normalized.match(/\b(ilk|birinci|ikinci|ucuncu|dorduncu|besinci|altinci|yedinci|sekizinci|dokuzuncu|onuncu)\s+sayfa(?:sinda|sinde|da|de|yi|i|ya|ye)?\b/);
  return ordinalMatch ? ordinalPages[ordinalMatch[1]] || 0 : 0;
};

const normalizeLiveContextList = (value: any) => {
  if (!Array.isArray(value)) return value ? [String(value)] : [];

  return value
    .map((item) => String(item?.gainId || item?.id || item?.url || item?.videoUrl || item?.pdfUrl || item || ""))
    .filter(Boolean);
};

const buildAITeacherLiveContextKey = (liveContext: Record<string, any> = {}) => {
  // SADECE büyük bağlam değişikliklerini key'e dahil et — kazanımlar arası geçişte (currentGainIndex)
  // ve ufak metadata değişikliklerinde Live bağlantı koparılmasın, konuşma geçmişi sıfırlanmasın.
  return JSON.stringify({
    source: liveContext.source || "",
    lessonId: liveContext.lessonId || liveContext.lessonTitle || "",
    topicId: liveContext.topicId || "",
    contentId: liveContext.contentId || "",
    // Tüm kazanım listesi — bireysel currentGainIndex değişimi kabul edilir, listenin kendisi değişirse yeni oturum
    gainIds: normalizeLiveContextList(liveContext.gainIds),
    sessionPlanId: liveContext.sessionPlanId || "",
  });
};

const buildAITeacherLiveClientContent = (text: string) => ({
  turns: [
    {
      role: "user" as const,
      parts: [{ text: String(text || "").trim() || "Devam edelim." }],
    },
  ],
  turnComplete: true,
});

const timecodeToSeconds = (timecode?: string) => {
  if (!timecode) return 0;

  const parts = String(timecode)
    .split(":")
    .map((part) => Number.parseInt(part, 10));

  if (parts.some((part) => Number.isNaN(part))) return 0;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];

  return 0;
};

const getVimeoId = (url: string) => {
  const cleaned = String(url || "").trim().replace(/^@+/, "");
  if (/^\d{6,}$/.test(cleaned)) return cleaned;
  const match = cleaned.match(/(?:player\.vimeo\.com\/video\/|vimeo\.com\/(?:.*\/)?(?:video\/)?)(\d{6,})/);
  return match?.[1] || "";
};

const getYoutubeId = (url: string) => {
  const cleaned = String(url || "").trim().replace(/^@+/, "");
  const match =
    cleaned.match(/[?&]v=([A-Za-z0-9_-]{11})/) ||
    cleaned.match(/youtu\.be\/([A-Za-z0-9_-]{11})/) ||
    cleaned.match(/youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{11})/);
  return match?.[1] || "";
};

const resolveTutorMediaUrl = (url: string) => {
  const cleaned = String(url || "").trim().replace(/^@+/, "");
  if (!cleaned || /^https?:\/\//i.test(cleaned)) return cleaned;

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";
  if (!apiBaseUrl) return cleaned;

  try {
    const apiUrl = new URL(apiBaseUrl, window.location.origin);
    if (cleaned.startsWith("/api/")) return `${apiUrl.origin}${cleaned}`;
    return `${apiBaseUrl.replace(/\/$/, "")}${cleaned.startsWith("/") ? cleaned : `/${cleaned}`}`;
  } catch (_) {
    return cleaned;
  }
};

const ShinyStatusBubble = ({ text }: { text: string }) => (
  <div className="flex justify-start">
    <style>
      {`
        @keyframes aiTutorShine {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(140%); }
        }
        .ai-tutor-shiny::after {
          content: "";
          position: absolute;
          inset: 0;
          width: 55%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent);
          animation: aiTutorShine 1.45s ease-in-out infinite;
        }
      `}
    </style>
    <div className="ai-tutor-shiny relative overflow-hidden rounded-2xl rounded-tl-md border border-orange-100 bg-orange-50 px-3 py-2 text-sm font-medium text-orange-700 shadow-sm">
      <span className="relative z-10 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        {text}
      </span>
    </div>
  </div>
);

const VoiceBubbleControl = ({
  expanded,
  animating,
  disabled,
  level,
  onClick,
  onCancel,
}: {
  expanded: boolean;
  animating: boolean;
  disabled: boolean;
  level: number;
  onClick: () => void;
  onCancel?: () => void;
}) => {
  const safeLevel = Math.min(1, Math.max(AI_TUTOR_VOICE_LEVEL_IDLE, Number(level) || AI_TUTOR_VOICE_LEVEL_IDLE));

  return (
    <>
      <style>
        {`
          .ai-tutor-voice-control {
            --voice-level: 0.08;
            overflow: hidden;
            transition:
              width 320ms cubic-bezier(0.22, 1, 0.36, 1),
              flex-basis 320ms cubic-bezier(0.22, 1, 0.36, 1),
              background-color 220ms ease,
              border-radius 220ms ease,
              box-shadow 220ms ease;
          }

          .ai-tutor-voice-mic {
            transform: scale(calc(1 + (var(--voice-level) * 0.12)));
            box-shadow:
              inset 0 0 0 1px rgba(255,255,255,0.34),
              0 0 0 1.5px rgba(255,255,255,0.18);
          }

          .ai-tutor-voice-ring {
            content: "";
            position: absolute;
            inset: -3px;
            border-radius: 1rem;
            border: 2px solid rgba(255,255,255,0.72);
            opacity: 0;
            transform: scale(0.94);
            transition: opacity 180ms ease, transform 180ms ease;
            pointer-events: none;
          }

          .ai-tutor-voice-control.is-animating .ai-tutor-voice-ring {
            opacity: 0.95;
            transform: scale(calc(1.02 + (var(--voice-level) * 0.28)));
            box-shadow: 0 0 calc(4px + (var(--voice-level) * 12px)) rgba(255,255,255,0.32);
          }

          .ai-tutor-voice-dot {
            transform: scale(1);
            transition: transform 220ms ease, opacity 220ms ease;
          }

          .ai-tutor-voice-control.is-animating .ai-tutor-voice-dot {
            animation: aiTutorVoiceDot 760ms ease-in-out infinite;
          }

          .ai-tutor-voice-control.is-animating .ai-tutor-voice-dot:nth-child(2) { animation-delay: 90ms; }
          .ai-tutor-voice-control.is-animating .ai-tutor-voice-dot:nth-child(3) { animation-delay: 180ms; }
          .ai-tutor-voice-control.is-animating .ai-tutor-voice-dot:nth-child(4) { animation-delay: 270ms; }

          @keyframes aiTutorVoiceDot {
            0%, 100% { transform: scaleY(0.78); opacity: 0.82; }
            45% { transform: scaleY(calc(1.05 + (var(--voice-level) * 0.42))); opacity: 1; }
          }
        `}
      </style>
      <div
        className={`ai-tutor-voice-control ${animating ? "is-animating" : ""} flex h-12 items-center bg-orange-500 shadow-sm ${
          expanded ? "flex-1 gap-2 rounded-[24px] px-2" : "w-12 flex-none gap-0 rounded-2xl px-0"
        }`}
        style={{ "--voice-level": safeLevel } as any}
      >
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          title={expanded ? "Kaydı durdur" : "Mikrofonla konuş"}
          aria-label={expanded ? "Kaydı durdur" : "Mikrofonla konuş"}
          className={`ai-tutor-voice-mic relative flex flex-shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-white transition-all duration-300 ease-out hover:bg-orange-600 disabled:cursor-default disabled:opacity-80 ${
            expanded ? "h-9 w-9" : "h-12 w-12"
          }`}
        >
          <span className="ai-tutor-voice-ring" />
          <Mic className={`relative z-10 transition-all duration-300 ${expanded ? "h-4 w-4" : "h-5 w-5"}`} />
        </button>
        <span
          className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 px-2 transition-all duration-300 ease-out ${
            expanded ? "scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0"
          }`}
        >
          {[0, 1, 2, 3].map((dot) => (
            <span
              key={dot}
              className="ai-tutor-voice-dot h-6 w-4 rounded-full bg-white shadow-[0_1px_5px_rgba(0,0,0,0.16)]"
            />
          ))}
        </span>
        <button
          type="button"
          onClick={onCancel}
          title="Sesli modu kapat"
          aria-label="Sesli modu kapat"
          tabIndex={expanded ? 0 : -1}
          className={`flex h-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/16 text-white transition-all duration-300 ease-out hover:bg-white/24 ${
            expanded ? "w-8 opacity-100" : "pointer-events-none w-0 opacity-0"
          }`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </>
  );
};

const AITutor = ({
  isOpen,
  context,
  onClose,
  sidebarExpanded = true,
  onShowPdfOnCanvas,
  onOpenSimulation,
}: AITutorProps) => {
  const [messages, setMessages] = useState<TutorMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [audioMode, setAudioMode] = useState<"live" | "tts" | "browser" | "none">("tts"); // Kullanıcının seçtiği AI öğretmen ses modeli
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceBubbleActive, setVoiceBubbleActive] = useState(false);
  const [voiceConversationActive, setVoiceConversationActive] = useState(false);
  const [voiceLevel, setVoiceLevel] = useState(AI_TUTOR_VOICE_LEVEL_IDLE);
  const [isPreparingSpeech, setIsPreparingSpeech] = useState(false);
  const [speechNeedsGesture, setSpeechNeedsGesture] = useState(false);
  const [speechError, setSpeechError] = useState("");
  const [lastAssistantMessage, setLastAssistantMessage] = useState("");
  const [isTypingMessage, setIsTypingMessage] = useState(false);
  const [isGainCardExpanded, setIsGainCardExpanded] = useState(false);
  const [activeMaterial, setActiveMaterial] = useState<ActiveMaterial>({ type: "empty" });
  const [resolvedContext, setResolvedContext] = useState<Record<string, any>>(context);
  const pendingSpeechReadyRef = useRef<{ durationMs: number } | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingSilenceIntervalRef = useRef<number | null>(null);
  const recordingMaxTimeoutRef = useRef<number | null>(null);
  const recordingSilenceStartRef = useRef<number | null>(null);
  const recordingSpeechDetectedRef = useRef(false);
  const recordingAudioContextRef = useRef<AudioContext | null>(null);
  const recordingAudioSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const stopRecordingRef = useRef<() => void>(() => undefined);
  // Tamamlanmış quiz'in kazanımı — move_to_next_gain gelene kadar aynı teste tekrar açılmasını engeller
  const completedTestGainIdRef = useRef<string | null>(null);
  // Son kazanım ilerleme zamanı — quiz-geçiş + move_to_next_gain aynı anda tetiklenip kazanım ATLAMASINI engeller
  const lastGainAdvanceAtRef = useRef<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const streamAudioContextRef = useRef<AudioContext | null>(null);
  const streamAudioSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const streamPlaybackTimeRef = useRef(0);
  const pcmRemainderRef = useRef<Uint8Array | null>(null);
  const hasQueuedAudibleStreamRef = useRef(false);
  const voiceLevelResetTimerRef = useRef<number | null>(null);
  const speechRequestIdRef = useRef(0);
  const liveConnectionRef = useRef<AITeacherLiveConnection | null>(null);
  const voiceEnabledRef = useRef(voiceEnabled);
  const voiceConversationActiveRef = useRef(false);
  const voiceCancelRequestedRef = useRef(false);
  const typingFlushRef = useRef<(() => void) | null>(null);
  const typingCancelRef = useRef<(() => void) | null>(null);
  const activeTypingMessageRef = useRef<{ id: string; fullText: string } | null>(null);
  const startedRef = useRef(false);
  const messageIdRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioModeRef = useRef<"live" | "tts" | "browser" | "none">("tts");
  // Başarısız pekiştirme testinin kazanımı — öğrenci geçemeden model move_to_next_gain çağırsa bile İLERLEME KİLİTLİ
  const lastFailedTestGainIdRef = useRef<string | null>(null);
  // Ekranda AÇIK olan pekiştirme testinin soruları — modele context'te verilir ki soru içeriği/numara uydurmasın
  const openTestQuestionsRef = useRef<Array<{ number: number; text: string; options?: Record<string, string>; correctAnswer?: string }>>([]);
  // Test açılmadan ÖNCE öğrenci sesli konuşuyor muydu? Test bitince sesi ancak o zaman geri aç (text kullanıcıyı zorlama)
  const voiceActiveBeforeTestRef = useRef(false);
  // Sohbet oturumu kimliği — gözlem/transkript gruplaması için (panel açılınca/sıfırlanınca yenilenir)
  const conversationIdRef = useRef<string>("");
  const newConversationId = () => {
    try {
      if (typeof crypto !== "undefined" && (crypto as any).randomUUID) return `conv_${(crypto as any).randomUUID()}`;
    } catch {}
    return `conv_${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
  };
  const lastUserMessageRef = useRef<string>("");
  // OTORİTER kazanım index'i (senkron): sunucu yanıtındaki eski context yankısı ilerlemeyi geri EZEMEZ
  const gainIndexRef = useRef<number>(Number((context as any)?.currentGainIndex) || 0);
  const mergeServerContext = (nextContext: Record<string, any>) => ({
    ...nextContext,
    currentGainIndex: Math.max(Number(nextContext?.currentGainIndex) || 0, gainIndexRef.current),
  });

  const createMessageId = () => {
    messageIdRef.current += 1;
    return `ai-tutor-message-${messageIdRef.current}`;
  };

  const updateVoiceLevel = (level: number) => {
    const normalizedLevel = Math.min(1, Math.max(AI_TUTOR_VOICE_LEVEL_IDLE, Number(level) || AI_TUTOR_VOICE_LEVEL_IDLE));
    setVoiceLevel((currentLevel) => Math.max(normalizedLevel, currentLevel * 0.72));

    if (voiceLevelResetTimerRef.current !== null) {
      window.clearTimeout(voiceLevelResetTimerRef.current);
    }
    voiceLevelResetTimerRef.current = window.setTimeout(() => {
      setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
      voiceLevelResetTimerRef.current = null;
    }, 220);
  };

  const flushCurrentAssistantTyping = () => {
    if (typingFlushRef.current) {
      typingFlushRef.current();
    } else if (activeTypingMessageRef.current?.fullText) {
      const { id, fullText } = activeTypingMessageRef.current;
      setMessages((prev) =>
        prev.map((message) =>
          message.id === id ? { ...message, content: fullText } : message,
        ),
      );
    }
    activeTypingMessageRef.current = null;
    typingFlushRef.current = null;
    typingCancelRef.current = null;
    pendingSpeechReadyRef.current = null;
    setIsTypingMessage(false);
  };

  const stopCurrentAudio = () => {
    speechRequestIdRef.current += 1;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }

    streamAudioSourcesRef.current.forEach((source) => {
      try {
        source.stop();
      } catch {
        // Source may already be stopped.
      }
    });
    streamAudioSourcesRef.current.clear();
    streamPlaybackTimeRef.current = 0;
    pcmRemainderRef.current = null;
    hasQueuedAudibleStreamRef.current = false;
    pendingSpeechReadyRef.current = null;
    setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
    if (voiceLevelResetTimerRef.current !== null) {
      window.clearTimeout(voiceLevelResetTimerRef.current);
      voiceLevelResetTimerRef.current = null;
    }

    const streamAudioContext = streamAudioContextRef.current;
    streamAudioContextRef.current = null;
    if (streamAudioContext && streamAudioContext.state !== "closed") {
      streamAudioContext.close().catch(() => undefined);
    }
  };

  const closeLiveConnection = () => {
    const connection = liveConnectionRef.current;
    liveConnectionRef.current = null;
    if (!connection?.socket) return;

    connection.socket.onmessage = null;
    connection.socket.onerror = null;
    connection.socket.onclose = null;
    if (connection.socket.readyState === WebSocket.OPEN || connection.socket.readyState === WebSocket.CONNECTING) {
      connection.socket.close();
    }
  };

  const getStreamAudioContext = (sampleRate: number) => {
    const AudioContextClass = window.AudioContext || (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) throw new Error("Tarayıcı anlık ses oynatmayı desteklemiyor.");

    let audioContext = streamAudioContextRef.current;
    if (!audioContext || audioContext.state === "closed") {
      audioContext = new AudioContextClass({ sampleRate });
      streamAudioContextRef.current = audioContext;
      streamPlaybackTimeRef.current = 0;
    }

    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => undefined);
    }

    return audioContext;
  };

  const queuePcmAudioChunk = (chunk: Uint8Array, sampleRate: number) => {
    let pcmChunk = chunk;
    if (pcmRemainderRef.current?.byteLength) {
      const combined = new Uint8Array(pcmRemainderRef.current.byteLength + chunk.byteLength);
      combined.set(pcmRemainderRef.current, 0);
      combined.set(chunk, pcmRemainderRef.current.byteLength);
      pcmChunk = combined;
      pcmRemainderRef.current = null;
    }

    const usableByteLength = pcmChunk.byteLength - (pcmChunk.byteLength % 2);
    if (usableByteLength < pcmChunk.byteLength) {
      pcmRemainderRef.current = pcmChunk.slice(usableByteLength);
    }
    if (usableByteLength <= 0) return 0;

    let audioStartByte = 0;
    if (!hasQueuedAudibleStreamRef.current) {
      const silenceScanView = new DataView(pcmChunk.buffer, pcmChunk.byteOffset, usableByteLength);
      let firstAudibleSample = -1;
      for (let index = 0; index < usableByteLength / 2; index += 1) {
        if (Math.abs(silenceScanView.getInt16(index * 2, true)) > 450) {
          firstAudibleSample = index;
          break;
        }
      }

      if (firstAudibleSample < 0) return 0;

      const prerollSamples = Math.floor(sampleRate * 0.035);
      audioStartByte = Math.max(0, firstAudibleSample - prerollSamples) * 2;
      hasQueuedAudibleStreamRef.current = true;
    }

    const audioByteLength = usableByteLength - audioStartByte;
    if (audioByteLength <= 0) return 0;

    const audioContext = getStreamAudioContext(sampleRate);
    const sampleCount = audioByteLength / 2;
    const audioBuffer = audioContext.createBuffer(1, sampleCount, sampleRate);
    const channelData = audioBuffer.getChannelData(0);
    const view = new DataView(pcmChunk.buffer, pcmChunk.byteOffset + audioStartByte, audioByteLength);
    let squareSum = 0;

    for (let index = 0; index < sampleCount; index += 1) {
      const sample = Math.max(-1, Math.min(1, view.getInt16(index * 2, true) / 32768));
      channelData[index] = sample;
      squareSum += sample * sample;
    }
    updateVoiceLevel(Math.sqrt(squareSum / sampleCount) * 5.5);

    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioContext.destination);

    const now = audioContext.currentTime;
    // Daha geniş buffer (120ms lookahead) — ağ jitter'ı nedeniyle ses kesintilerini önler
    const minLookahead = 0.12;
    const startAt = Math.max(streamPlaybackTimeRef.current || now + minLookahead, now + minLookahead);
    source.start(startAt);
    streamPlaybackTimeRef.current = startAt + audioBuffer.duration;

    streamAudioSourcesRef.current.add(source);
    source.onended = () => {
      streamAudioSourcesRef.current.delete(source);
    };

    return audioBuffer.duration * 1000;
  };

  const cleanupRecordingMonitor = () => {
    if (recordingSilenceIntervalRef.current !== null) {
      window.clearInterval(recordingSilenceIntervalRef.current);
      recordingSilenceIntervalRef.current = null;
    }
    if (recordingMaxTimeoutRef.current !== null) {
      window.clearTimeout(recordingMaxTimeoutRef.current);
      recordingMaxTimeoutRef.current = null;
    }

    try {
      recordingAudioSourceRef.current?.disconnect();
    } catch {
      // Source may already be disconnected.
    }
    recordingAudioSourceRef.current = null;

    const audioContext = recordingAudioContextRef.current;
    recordingAudioContextRef.current = null;
    if (audioContext && audioContext.state !== "closed") {
      audioContext.close().catch(() => undefined);
    }

    recordingSilenceStartRef.current = null;
    recordingSpeechDetectedRef.current = false;
    setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
  };

  const startRecordingSilenceMonitor = (stream: MediaStream) => {
    recordingMaxTimeoutRef.current = window.setTimeout(() => stopRecordingRef.current(), AI_TUTOR_RECORDING_MAX_MS);

    const AudioContextClass = window.AudioContext || (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    const samples = new Uint8Array(analyser.fftSize);
    source.connect(analyser);

    recordingAudioContextRef.current = audioContext;
    recordingAudioSourceRef.current = source;
    recordingSilenceStartRef.current = null;
    recordingSpeechDetectedRef.current = false;

    recordingSilenceIntervalRef.current = window.setInterval(() => {
      analyser.getByteTimeDomainData(samples);
      let squareSum = 0;

      for (let index = 0; index < samples.length; index += 1) {
        const centered = (samples[index] - 128) / 128;
        squareSum += centered * centered;
      }

      const rms = Math.sqrt(squareSum / samples.length);
      updateVoiceLevel((rms - 0.012) * 11);
      const now = Date.now();
      if (rms >= AI_TUTOR_RECORDING_SPEECH_RMS) {
        recordingSpeechDetectedRef.current = true;
        recordingSilenceStartRef.current = null;
        return;
      }

      if (!recordingSpeechDetectedRef.current) return;
      if (!recordingSilenceStartRef.current) {
        recordingSilenceStartRef.current = now;
        return;
      }

      if (now - recordingSilenceStartRef.current >= AI_TUTOR_RECORDING_SILENCE_MS) {
        stopRecordingRef.current();
      }
    }, 180);
  };

  const playBrowserSpeechFallback = (text: string, onReady?: (durationMs: number) => void) => {
    if (!("speechSynthesis" in window)) {
      throw new Error("Tarayıcı sesli okuma desteklemiyor.");
    }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const turkishVoice = voices.find((voice) => voice.lang?.toLowerCase().startsWith("tr"));
    if (turkishVoice) {
      utterance.voice = turkishVoice;
    }
    utterance.lang = "tr-TR";
    utterance.rate = 0.95;
    utterance.pitch = 1;
    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPreparingSpeech(false);
    };
    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPreparingSpeech(false);
    };
    utterance.onerror = () => {
      setIsSpeaking(false);
      setIsPreparingSpeech(false);
      setSpeechNeedsGesture(true);
    };
    onReady?.(estimateSpeechDurationMs(text));
    window.speechSynthesis.speak(utterance);
  };

  const getRequestContext = () => ({
    ...context,
    ...resolvedContext,
    gainId: resolvedContext.gainId || context.gainId,
    gainIds: resolvedContext.gainIds || context.gainIds,
    gains: resolvedContext.gains || context.gains,
    gainText: resolvedContext.gainText || context.gainText,
    topicTitle: resolvedContext.topicTitle || context.topicTitle,
    contentTitle: resolvedContext.contentTitle || context.contentTitle,
    lessonTitle: resolvedContext.lessonTitle || context.lessonTitle,
    unitTitle: resolvedContext.unitTitle || context.unitTitle,
    topicId: resolvedContext.topicId || context.topicId,
    contentId: resolvedContext.contentId || context.contentId,
    pdfUrl: resolvedContext.pdfUrl || context.pdfUrl,
    relatedMaterials: resolvedContext.relatedMaterials || context.relatedMaterials,
    source: resolvedContext.source || context.source,
    // sessionPlanId her zaman açıkça koru — büyük resolvedContext içinde kaybolmasın
    sessionPlanId: context.sessionPlanId || resolvedContext.sessionPlanId || undefined,
    // sessionPlanCtx büyük obje — backend'e gönderme, sadece sessionPlanId yeterli
    sessionPlanCtx: undefined,
    // Ekranda açık pekiştirme testi varsa soruları modele ver (soru içeriği/numara uydurmasın)
    openTestQuestions: openTestQuestionsRef.current.length ? openTestQuestionsRef.current : undefined,
    // Gözlem: sohbet oturumu kimliği (transkript gruplaması)
    conversationId: conversationIdRef.current || undefined,
  });

  const topicGainPlan = Array.isArray(resolvedContext.relatedMaterials?.gainPlan)
    ? resolvedContext.relatedMaterials.gainPlan
    : [];
  const currentGainIndex = topicGainPlan.length
    ? Math.min(Math.max(0, Number(resolvedContext.currentGainIndex) || 0), topicGainPlan.length - 1)
    : 0;
  const activePlannedGain = topicGainPlan[currentGainIndex] || null;
  const hasTopicGainPlan = topicGainPlan.length > 1;

  const enhanceToolCallsForAssistantMessage = (
    assistantText: string,
    toolCalls: AITutorToolCall[] = [],
    toolContext: Record<string, any> = resolvedContext,
  ) => {
    const workspaceToolNames = new Set(["show_pdf_on_canvas", "open_lesson_video", "open_simulation", "open_practice_test"]);
    const hasPdfTool = toolCalls.some((toolCall) => toolCall.name === "show_pdf_on_canvas");
    const mentionedPageNumber = parseMentionedPdfPageNumber(assistantText);
    const normalizedAssistantText = String(assistantText || "").toLocaleLowerCase("tr-TR");
    const mentionsPdfPage =
      mentionedPageNumber > 0 ||
      /\b(pdf|materyal|sayfa)\b.{0,100}\b(sayfa|aç|gör|incele)/i.test(normalizedAssistantText);
    if (hasPdfTool && mentionedPageNumber > 0) {
      return toolCalls.map((toolCall) =>
        toolCall.name === "show_pdf_on_canvas"
          ? {
            ...toolCall,
            arguments: {
              ...toolCall.arguments,
              page_number: mentionedPageNumber,
            },
          }
          : toolCall,
      );
    }
    if (!mentionsPdfPage || hasPdfTool) return toolCalls;

    const gainPlan = Array.isArray(toolContext.relatedMaterials?.gainPlan) ? toolContext.relatedMaterials.gainPlan : [];
    const plannedGainIndex = gainPlan.length
      ? Math.min(Math.max(0, Number(toolContext.currentGainIndex) || 0), gainPlan.length - 1)
      : 0;
    const plannedPdf = gainPlan[plannedGainIndex]?.pdf || (!gainPlan.length ? toolContext.relatedMaterials?.pdfs?.[0] : null);
    const pdfUrl = plannedPdf?.url || (!gainPlan.length ? toolContext.pdfUrl : "");
    if (!pdfUrl) return toolCalls;

    const pdfToolCall: AITutorToolCall = {
      id: "ui-pdf-open-from-message",
      name: "show_pdf_on_canvas",
      arguments: {
        pdf_url: pdfUrl,
        title: plannedPdf?.title || toolContext.contentTitle || "PDF Materyali",
        total_pages: Number(plannedPdf?.totalPages || 0),
        page_number: Number(mentionedPageNumber || plannedPdf?.pageNumber || 1),
      },
    };

    return [pdfToolCall, ...toolCalls.filter((toolCall) => !workspaceToolNames.has(toolCall.name))];
  };

  useEffect(() => {
    voiceEnabledRef.current = voiceEnabled;
    if (!voiceEnabled) {
      setVoiceConversationActive(false);
      voiceConversationActiveRef.current = false;
      stopRecordingRef.current?.();
      closeLiveConnection();
    }
  }, [voiceEnabled]);

  // Kullanıcının seçtiği AI öğretmen ses modelini fetch et (live veya tts)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const userPreference = (() => {
        try {
          const stored = localStorage.getItem("aiTutorAudioMode");
          return stored === "live" || stored === "tts" ? stored : null;
        } catch {
          return null;
        }
      })();

      try {
        const settings = await ApiService.getAITeacherSettings();
        const mode = String(settings?.selectedAudioModel?.mode || "tts");
        if (cancelled) return;
        const adminMode = mode === "live" ? "live" : mode === "browser" ? "browser" : mode === "none" ? "none" : "tts";
        // Admin 'Sessiz' veya 'Tarayıcı Sesi' seçtiyse öğrenci tercihi (localStorage) EZİLİR;
        // sesli modlarda (tts/live) öğrencinin kendi live/tts tercihi geçerli kalır
        const resolved = adminMode === "none" || adminMode === "browser" ? adminMode : (userPreference || adminMode);
        setAudioMode(resolved);
        audioModeRef.current = resolved;
        if (resolved === "none") {
          // Sessiz mod: otomatik seslendirme tamamen kapalı
          setVoiceEnabled(false);
        }
      } catch {
        // Fetch başarısız olursa öğrenci tercihi ya da varsayılan 'tts' kalır
        if (userPreference && !cancelled) {
          setAudioMode(userPreference);
          audioModeRef.current = userPreference;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const toggleAudioMode = () => {
    const nextMode: "live" | "tts" = audioMode === "live" ? "tts" : "live";
    setAudioMode(nextMode);
    audioModeRef.current = nextMode;
    try {
      localStorage.setItem("aiTutorAudioMode", nextMode);
    } catch {
      // localStorage erişilemezse sessizce geç
    }
    if (nextMode === "tts") {
      // Live → TTS: açık socket'i kapat, sıradaki mesaj TTS ile gelir
      closeLiveConnection();
    }
    // TTS → Live: sıradaki mesajda otomatik olarak yeni live socket açılır
  };

  useEffect(() => {
    voiceConversationActiveRef.current = voiceConversationActive;
  }, [voiceConversationActive]);

  useEffect(() => {
    if (voiceConversationActive || isRecording || isSpeaking || isPreparingSpeech || (voiceBubbleActive && (isLoading || isTypingMessage))) {
      setVoiceBubbleActive(true);
      return;
    }

    const collapseTimer = window.setTimeout(() => setVoiceBubbleActive(false), 900);
    return () => window.clearTimeout(collapseTimer);
  }, [voiceConversationActive, isRecording, isSpeaking, isPreparingSpeech, isLoading, isTypingMessage, voiceBubbleActive]);

  useEffect(() => {
    if (!isOpen) {
      startedRef.current = false;
      typingCancelRef.current?.();
      activeTypingMessageRef.current = null;
      typingFlushRef.current = null;
      typingCancelRef.current = null;
      setMessages([]);
      setInput("");
      setResolvedContext(context);
      gainIndexRef.current = Number((context as any)?.currentGainIndex) || 0;
      openTestQuestionsRef.current = [];
      setActiveMaterial({ type: "empty" });
      setIsSpeaking(false);
      setIsPreparingSpeech(false);
      setSpeechNeedsGesture(false);
      setSpeechError("");
      setLastAssistantMessage("");
      setIsTypingMessage(false);
      setVoiceBubbleActive(false);
      setVoiceConversationActive(false);
      voiceConversationActiveRef.current = false;
      voiceCancelRequestedRef.current = false;
      setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
      stopCurrentAudio();
      closeLiveConnection();
      cleanupRecordingMonitor();
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
      recorderRef.current = null;
      return;
    }

    if (!startedRef.current) {
      startedRef.current = true;
      conversationIdRef.current = newConversationId(); // yeni panel oturumu = yeni sohbet kaydı grubu
      setResolvedContext(context);
      gainIndexRef.current = Number((context as any)?.currentGainIndex) || 0;
      // AudioContext'i hemen başlat — kullanıcı butona basınca gesture var,
      // Live mod'un ilk audio chunkı geldiğinde gesture timeout olmasın
      try {
        getStreamAudioContext(24000);
      } catch {}

      const sessionPlanId = context?.sessionPlanId;
      if (sessionPlanId) {
        (async () => {
          let resumeOverride: Record<string, any> | undefined;
          let hasRestoredHistory = false;
          try {
            const plan = await ApiService.getSessionPlan(String(sessionPlanId));
            const savedIndex = Number(plan?.currentGainIndex) || 0;
            if (savedIndex > 0) {
              setResolvedContext((prev) => ({ ...prev, currentGainIndex: savedIndex }));
              gainIndexRef.current = savedIndex;
              resumeOverride = { currentGainIndex: savedIndex };
            }

            const savedHistory = Array.isArray(plan?.history) ? plan.history : [];
            if (savedHistory.length > 0) {
              const restoredMessages: TutorMessage[] = savedHistory.map((entry) => ({
                id: createMessageId(),
                role: entry.role === "assistant" ? "assistant" : "user",
                content: String(entry.content || ""),
              }));
              setMessages(restoredMessages);
              const lastAssistant = [...restoredMessages].reverse().find((m) => m.role === "assistant");
              if (lastAssistant) setLastAssistantMessage(lastAssistant.content);
              hasRestoredHistory = true;
            }
          } catch {
            // Plan yüklenemezse sıfırdan başla
          } finally {
            if (!hasRestoredHistory) {
              void sendMessage("", resumeOverride ? { contextOverride: resumeOverride } : undefined);
            }
          }
        })();
      } else {
        void sendMessage("");
      }
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isLoading]);

  // Her asistan cevabı tamamlandıktan sonra konuşma geçmişini SessionPlan'a kaydet.
  // Streaming sırasında flood etmesin diye debounced.
  useEffect(() => {
    if (!isOpen) return;
    const sessionPlanId = context?.sessionPlanId || resolvedContext?.sessionPlanId;
    if (!sessionPlanId) return;
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last?.role !== "assistant" || !last.content) return;
    if (isTypingMessage) return;

    const handle = window.setTimeout(() => {
      const history = messages
        .filter((m) => m.content && !m.transient)
        .map((m) => ({ role: m.role as "user" | "assistant", content: m.content }));
      void ApiService.updateSessionPlanProgress(String(sessionPlanId), { history }).catch(() => {
        // History kaydedilemezse sessizce geç — kullanıcı akışı bozulmasın
      });
    }, 1500);

    return () => window.clearTimeout(handle);
  }, [messages, isTypingMessage, isOpen, context?.sessionPlanId, resolvedContext?.sessionPlanId]);

  const playTtsAudio = async (
    text: string,
    options: {
      requireGestureFallback?: boolean;
      onReady?: (durationMs: number) => void;
    } = {},
  ) => {
    const { requireGestureFallback = true, onReady } = options;
    const cleanText = text.trim();
    if (!cleanText) return;

    // Sessiz mod: HİÇ ses yok — ne API ne cihaz sesi. Metin beklemeden akar.
    if (audioModeRef.current === "none") {
      setIsPreparingSpeech(false);
      onReady?.(estimateSpeechDurationMs(cleanText));
      return;
    }

    // Tarayıcı sesi modu: API'ye hiç gitme, cihazda seslendir (maliyet sıfır).
    if (audioModeRef.current === "browser") {
      try {
        setSpeechError("");
        setSpeechNeedsGesture(false);
        stopCurrentAudio();
        playBrowserSpeechFallback(cleanText, onReady);
      } catch {
        setSpeechError("Tarayıcı sesli okumayı desteklemiyor.");
        setIsPreparingSpeech(false);
        onReady?.(estimateSpeechDurationMs(cleanText));
      }
      return;
    }

    try {
      setIsPreparingSpeech(true);
      setSpeechError("");
      setSpeechNeedsGesture(false);
      setIsSpeaking(false);

      stopCurrentAudio();
      const requestId = speechRequestIdRef.current;
      try {
        getStreamAudioContext(24000);
      } catch {
        // Non-stream fallback will surface a readable error if browser audio is unavailable.
      }

      let streamReadyNotified = false;
      let streamStarted = false;
      let textFallbackStarted = false;
      let streamTextFallbackTimer: number | null = window.setTimeout(() => {
        if (streamReadyNotified || requestId !== speechRequestIdRef.current) return;

        textFallbackStarted = true;
        streamReadyNotified = true;
        setIsPreparingSpeech(false);
        onReady?.(estimateSpeechDurationMs(cleanText));
      }, AI_TUTOR_SPEECH_STREAM_TEXT_FALLBACK_MS);

      const clearStreamTextFallbackTimer = () => {
        if (streamTextFallbackTimer === null) return;
        window.clearTimeout(streamTextFallbackTimer);
        streamTextFallbackTimer = null;
      };
      const notifyStreamReady = () => {
        if (streamReadyNotified || requestId !== speechRequestIdRef.current) return;
        clearStreamTextFallbackTimer();
        streamReadyNotified = true;
        setIsPreparingSpeech(false);
        setIsSpeaking(true);
        onReady?.(estimateSpeechDurationMs(cleanText));
      };
      const finishStreamPlayback = () => {
        const audioContext = streamAudioContextRef.current;
        const remainingMs = audioContext
          ? Math.max(0, (streamPlaybackTimeRef.current - audioContext.currentTime) * 1000)
          : 0;

        window.setTimeout(() => {
          if (requestId !== speechRequestIdRef.current) return;
          clearStreamTextFallbackTimer();
          setIsSpeaking(false);
          setIsPreparingSpeech(false);
        }, remainingMs + 150);
      };

      const streamResult = await ApiService.streamAITeacherSpeech(cleanText, {
        onChunk: (chunk, metadata) => {
          if (!voiceEnabledRef.current || requestId !== speechRequestIdRef.current) return;
          if (textFallbackStarted && !streamStarted) return;

          const queuedDurationMs = queuePcmAudioChunk(chunk, metadata.sampleRate);
          if (queuedDurationMs > 0) {
            streamStarted = true;
            if (textFallbackStarted) {
              clearStreamTextFallbackTimer();
              setIsPreparingSpeech(false);
              setIsSpeaking(true);
            } else {
              notifyStreamReady();
            }
          }
        },
        onEnd: finishStreamPlayback,
      });

      if (streamResult.streamed) {
        clearStreamTextFallbackTimer();
        if (!streamStarted) {
          setIsPreparingSpeech(false);
          if (!streamReadyNotified) onReady?.(estimateSpeechDurationMs(cleanText));
        }

        const audioContext = streamAudioContextRef.current;
        if (audioContext?.state !== "running" && requireGestureFallback) {
          setIsSpeaking(false);
          setSpeechNeedsGesture(true);
        }
        return;
      }

      clearStreamTextFallbackTimer();
      if (textFallbackStarted && !streamStarted) {
        setIsPreparingSpeech(false);
        return;
      }
      const audioBlob = streamResult.blob || await ApiService.generateAITeacherSpeech(cleanText);
      if (!voiceEnabledRef.current) {
        setIsPreparingSpeech(false);
        onReady?.(estimateSpeechDurationMs(cleanText));
        return;
      }
      if (textFallbackStarted && !streamStarted) {
        setIsPreparingSpeech(false);
        return;
      }
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audioUrlRef.current = audioUrl;

      let readyNotified = false;
      const notifyReady = () => {
        if (readyNotified) return;
        readyNotified = true;
        const durationMs = Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration * 1000
          : estimateSpeechDurationMs(cleanText);
        onReady?.(durationMs);
      };

      audio.onplay = () => setIsSpeaking(true);
      audio.onended = () => {
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
      };
      audio.onerror = () => {
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
        setSpeechError("Ses dosyası çalınamadı.");
        setSpeechNeedsGesture(true);
      };

      await new Promise<void>((resolve) => {
        if (audio.readyState >= 1) {
          notifyReady();
          resolve();
          return;
        }

        const timeoutId = window.setTimeout(() => {
          notifyReady();
          resolve();
        }, 1200);

        audio.onloadedmetadata = () => {
          window.clearTimeout(timeoutId);
          notifyReady();
          resolve();
        };
      });

      await audio.play();
    } catch (error) {
      const serverError = error instanceof Error ? error.message : "Ses üretilemedi.";
      try {
        if (!voiceEnabledRef.current || (audioModeRef.current as string) === "none") {
          // Sessiz modda hata fallback'i de SESSİZ — PC sesine düşme
          setIsPreparingSpeech(false);
          onReady?.(estimateSpeechDurationMs(cleanText));
          return;
        }
        playBrowserSpeechFallback(cleanText, onReady);
        console.warn("AI Öğretmen Gemini TTS çalışmadı, tarayıcı sesi kullanılıyor:", serverError);
        setSpeechError("");
      } catch {
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
        setSpeechError(serverError);
        if (requireGestureFallback) {
          setSpeechNeedsGesture(true);
        }
      }
    }
  };

  const loadPracticeTest = async (gainId: string, questionId?: string) => {
    if (!gainId) {
      setActiveMaterial({ type: "test-error", gainId: "", message: "Bu test için kazanım bilgisi bulunamadı." });
      return;
    }

    setActiveMaterial({ type: "test-loading", gainId });

    try {
      const recentMessages = messages
        .filter((m) => m.content?.trim())
        .slice(-6)
        .map((m) => ({ role: m.role, content: String(m.content).slice(0, 600) }));

      const session = await ApiService.getAITeacherPracticeTest({
        gainId,
        context: { ...getRequestContext(), recentMessages },
      });

      const questions = Array.isArray(session?.questions) ? session.questions : [];
      if (!questions.length) {
        openTestQuestionsRef.current = [];
        setActiveMaterial({ type: "test-error", gainId, message: session?.message || "Bu kazanım için gösterilecek test sorusu bulunamadı." });
        return;
      }

      // Modele verilecek grounding: numara + tam metin + (gerçek) şık + doğru cevap.
      // TYT sorularında şıklar metnin İÇİNDE (options sadece harf etiketi) → o durumda options'ı gönderme.
      openTestQuestionsRef.current = questions.map((q: any, i: number) => {
        const opts = q?.options && typeof q.options === "object" ? q.options : null;
        const hasRealOptions = !!opts && Object.entries(opts).some(
          ([label, text]) => String(text || "").trim() && String(text).trim().toUpperCase() !== String(label).toUpperCase(),
        );
        return {
          number: i + 1,
          text: String(q?.text || ""),
          options: hasRealOptions ? (opts as Record<string, string>) : undefined,
          correctAnswer: q?.correctAnswer ? String(q.correctAnswer) : undefined,
        };
      });

      setActiveMaterial({
        type: "test",
        gainId,
        questionId,
        questions,
        source: session?.source,
      });

      // Live modda sistem talimatı SADECE oturum açılışında kurulur; test sonradan açıldığında
      // live grounding donuk kalıp soruları görmez (AI uydurur/tekrar eder). Bağlantıyı kapat →
      // sonraki tur openTestQuestions içeren TAZE grounding ile yeniden kurulur.
      if (audioModeRef.current === "live") closeLiveConnection();
    } catch (error: any) {
      openTestQuestionsRef.current = [];
      setActiveMaterial({
        type: "test-error",
        gainId,
        message: error?.message || "Pekiştirme testi yüklenemedi.",
      });
    }
  };

  const applyToolCalls = (toolCalls: AITutorToolCall[] = [], toolContext: Record<string, any> = resolvedContext) => {
    const workspaceToolNames = new Set(["show_pdf_on_canvas", "open_lesson_video", "open_simulation", "open_practice_test"]);
    let didApplyWorkspaceTool = false;
    const gainPlan = Array.isArray(toolContext.relatedMaterials?.gainPlan) ? toolContext.relatedMaterials.gainPlan : [];
    let plannedGainIndex = gainPlan.length
      ? Math.min(Math.max(0, Number(toolContext.currentGainIndex) || 0), gainPlan.length - 1)
      : 0;
    // move_to_next_gain ÖNCE işlensin ki index güncellensin; sonraki workspace tool'ları (test/PDF) YENİ kazanıma açılsın
    const orderedCalls = [
      ...toolCalls.filter((c) => c.name === "move_to_next_gain"),
      ...toolCalls.filter((c) => c.name !== "move_to_next_gain"),
    ];

    orderedCalls.forEach((toolCall) => {
      const plannedGain = gainPlan[plannedGainIndex] || {}; // her iterasyonda GÜNCEL index'ten
      const isWorkspaceTool = workspaceToolNames.has(toolCall.name);
      if (isWorkspaceTool && didApplyWorkspaceTool) return;

      if (toolCall.name === "show_pdf_on_canvas") {
        const plannedPdf = plannedGain.pdf || {};
        const matchingRelatedPdf = Array.isArray(toolContext.relatedMaterials?.pdfs)
          ? toolContext.relatedMaterials.pdfs.find((pdf: any) => pdf?.url && pdf.url === (plannedPdf.url || toolCall.arguments?.pdf_url))
          : null;
        const relatedPdf = gainPlan.length
          ? {
            ...matchingRelatedPdf,
            ...plannedPdf,
            pageImages: Array.isArray(plannedPdf.pageImages) && plannedPdf.pageImages.length
              ? plannedPdf.pageImages
              : matchingRelatedPdf?.pageImages,
          }
          : (matchingRelatedPdf || toolContext.relatedMaterials?.pdfs?.[0] || {});
        const payload = {
          pdf_url: String(gainPlan.length ? (relatedPdf.url || "") : (toolCall.arguments?.pdf_url || relatedPdf.url || toolContext.pdfUrl || "")),
          title: String(gainPlan.length ? (relatedPdf.title || "") : (toolCall.arguments?.title || relatedPdf.title || "")),
          total_pages: Number(gainPlan.length ? (relatedPdf.totalPages || 0) : (toolCall.arguments?.total_pages || relatedPdf.totalPages || 0)),
          page_number: Number(gainPlan.length ? (relatedPdf.pageNumber || 1) : (toolCall.arguments?.page_number || relatedPdf.pageNumber || 1)),
          page_images: Array.isArray(relatedPdf.pageImages) ? relatedPdf.pageImages : [],
        };

        setActiveMaterial({
          type: "pdf",
          pdfUrl: payload.pdf_url,
          pageNumber: payload.page_number,
          title: payload.title,
          totalPages: payload.total_pages,
          pageImages: payload.page_images,
        });
        window.dispatchEvent(new CustomEvent("aiTutorShowPdfOnCanvas", { detail: payload }));
        onShowPdfOnCanvas?.(payload);
        didApplyWorkspaceTool = true;
      }

      if (toolCall.name === "open_lesson_video") {
        // Video açılınca AI konuşmayı/sesi durdursun, mikrofon da kapansın
        stopCurrentAudio();
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
        setIsTypingMessage(false);
        // Live bağlantı AÇIK kalsın — kullanıcı video izlediğinde de aynı oturumda devam etsin
        // Mikrofon ve konuşma modunu kapat — AI kendi sesini duyup cevap vermesin
        setVoiceConversationActive(false);
        voiceConversationActiveRef.current = false;
        setVoiceBubbleActive(false);
        cleanupRecordingMonitor();
        recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        recorderRef.current = null;
        setIsRecording(false);
        const primaryVideo = plannedGain.video || (!gainPlan.length ? toolContext.relatedMaterials?.primaryVideo : {}) || {};
        setActiveMaterial({
          type: "video",
          videoId: String(gainPlan.length ? (primaryVideo.id || "") : (toolCall.arguments?.video_id || primaryVideo.id || "")),
          videoUrl: String(gainPlan.length ? (primaryVideo.url || "") : (toolCall.arguments?.video_url || primaryVideo.url || "")),
          title: String(gainPlan.length ? (primaryVideo.title || "İlgili video") : (toolCall.arguments?.title || primaryVideo.title || "İlgili video")),
          startTime: String(gainPlan.length ? (primaryVideo.startTime || "00:00:00") : (toolCall.arguments?.start_time || primaryVideo.startTime || "00:00:00")),
          endTime: String(gainPlan.length ? (primaryVideo.endTime || "00:00:00") : (toolCall.arguments?.end_time || primaryVideo.endTime || "00:00:00")),
          transcriptExcerpt: String(gainPlan.length ? (primaryVideo.transcriptExcerpt || "") : (toolCall.arguments?.transcript_excerpt || primaryVideo.transcriptExcerpt || "")),
        });
        didApplyWorkspaceTool = true;
      }

      if (toolCall.name === "open_simulation") {
        const payload = {
          topic_id: String(toolCall.arguments?.topic_id || context.topicId || context.contentId || ""),
        };

        setActiveMaterial({ type: "simulation", topicId: payload.topic_id });
        window.dispatchEvent(new CustomEvent("aiTutorOpenSimulation", { detail: payload }));
        onOpenSimulation?.(payload);
        didApplyWorkspaceTool = true;
      }

      if (toolCall.name === "open_practice_test") {
        const aiGainId = toolCall.arguments?.gain_id ? String(toolCall.arguments.gain_id) : null;
        // Fix D: AI'ın gain_id'si GEÇERLİ bir plan kazanımıysa ona güven (index bir tur geride
        // kalsa bile doğru YENİ kazanıma test açılsın; eski koşul geçerli hedefi de eskiye çekiyordu).
        // Yalnızca eksik ya da plan-DIŞI bir id ise aktif kazanıma düzelt.
        const planGainIds = new Set((gainPlan || []).map((g: any) => g?.gainId).filter(Boolean));
        let correctedGainId = aiGainId;
        if (plannedGain?.gainId && (!aiGainId || !planGainIds.has(aiGainId))) {
          correctedGainId = plannedGain.gainId;
        }
        const finalGainId = String(correctedGainId || plannedGain.gainId || toolContext.gainId || context.gainId || "");
        // Test açılınca AI konuşmayı/sesi durdursun, mikrofon da kapansın
        stopCurrentAudio();
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
        setIsTypingMessage(false);
        // Test öncesi öğrenci sesli mi konuşuyordu? Test bitince sesi ancak o zaman geri açacağız.
        voiceActiveBeforeTestRef.current = voiceConversationActiveRef.current;
        // Live bağlantı AÇIK kalsın — test çözüldükten sonra aynı oturum devam etsin
        // Mikrofon ve konuşma modunu kapat — AI kendi sesini duyup cevap vermesin
        setVoiceConversationActive(false);
        voiceConversationActiveRef.current = false;
        setVoiceBubbleActive(false);
        cleanupRecordingMonitor();
        recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        recorderRef.current = null;
        setIsRecording(false);
        if (completedTestGainIdRef.current && finalGainId === completedTestGainIdRef.current) {
          // Bu kazanımın testi az önce tamamlandı; move_to_next_gain gelene kadar tekrar açma
          didApplyWorkspaceTool = true;
          return;
        }
        void loadPracticeTest(
          finalGainId,
          toolCall.arguments?.question_id ? String(toolCall.arguments.question_id) : undefined,
        );
        didApplyWorkspaceTool = true;
      }

      if (toolCall.name === "move_to_next_gain") {
        // AI bir sonraki kazanıma geçmeye karar verdi — index'i artır (workspace tool'lardan ÖNCE işlenir)
        // KİLİT: bu kazanımın pekiştirme testi BAŞARISIZ olduysa, öğrenci testi geçmeden veya
        // AÇIKÇA "diğer kazanıma geç" demeden model geçişi UYGULANMAZ (deterministik remediation garantisi)
        const currentGainId = String(gainPlan[plannedGainIndex]?.gainId || "");
        const studentAskedNext =
          /(diğer|diger|sıradaki|siradaki|bir sonraki|yeni|öteki|oteki|başka|baska)\s+(kazanım|kazanim|eksik|konu)/i.test(lastUserMessageRef.current) ||
          /(sıradakine|siradakine|sonrakine|diğerine|digerine)\s+geç/i.test(lastUserMessageRef.current);
        if (lastFailedTestGainIdRef.current && lastFailedTestGainIdRef.current === currentGainId && !studentAskedNext) {
          return;
        }
        // Cooldown: quiz-geçiş ile aynı anda gelirse kazanım ATLAMASINI engelle
        if (plannedGainIndex < gainPlan.length - 1 && Date.now() - lastGainAdvanceAtRef.current > 10000) {
          plannedGainIndex += 1;
          const nextIndex = plannedGainIndex;
          lastGainAdvanceAtRef.current = Date.now();
          gainIndexRef.current = nextIndex;
          setResolvedContext((prev) => ({ ...prev, currentGainIndex: nextIndex }));
          completedTestGainIdRef.current = null; // yeni kazanım → test tekrar açılabilir
          openTestQuestionsRef.current = []; // eski kazanımın açık test soruları context'ten düşsün
          setActiveMaterial({ type: "empty" });
          const sessionPlanId = toolContext.sessionPlanId;
          if (sessionPlanId) {
            void ApiService.updateSessionPlanProgress(String(sessionPlanId), { currentGainIndex: nextIndex }).catch(() => {});
          }
          // Fix A: Sesli (live) oturumun sistem talimatı SADECE açılışta kurulur; kazanım geçişinde
          // grounding donuk kalıp AI eski kazanımı anlatmaya devam ediyordu. Bağlantıyı kapat →
          // sonraki tur yeni kazanım grounding'iyle yeniden kurulur. (Sadece gerçek geçiş anında.)
          closeLiveConnection();
        }
        return;
      }
    });
  };

  const appendAssistantMessage = async (assistantMessage: string) => {
    let didStartTyping = false;
    const assistantMessageId = createMessageId();
    const startSyncedTyping = (durationMs?: number) => {
      if (didStartTyping) return;
      didStartTyping = true;
      setIsTypingMessage(true);
      let typingController: ReturnType<typeof streamAssistantText>;
      typingController = streamAssistantText(
        assistantMessage,
        (partialText) => {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === assistantMessageId ? { ...message, content: partialText } : message,
            ),
          );
        },
        () => {
          if (typingFlushRef.current === typingController.flush) {
            typingFlushRef.current = null;
            typingCancelRef.current = null;
            activeTypingMessageRef.current = null;
          }
          setIsTypingMessage(false);
        },
        durationMs,
      );
      typingFlushRef.current = typingController.flush;
      typingCancelRef.current = typingController.cancel;
    };

    setLastAssistantMessage(assistantMessage);
    activeTypingMessageRef.current = { id: assistantMessageId, fullText: assistantMessage };
    setMessages((prev) => [...prev, { id: assistantMessageId, role: "assistant", content: "" }]);

    if (voiceEnabled) {
      // Ses hazırsa o süreyle sync et
      const leadInTimer = window.setTimeout(() => {
        if (didStartTyping) return;
        const ready = pendingSpeechReadyRef.current;
        if (!ready) return;
        pendingSpeechReadyRef.current = null;
        startSyncedTyping(ready.durationMs);
      }, 500);

      // Ses 1800ms'de hâlâ gelmemişse metni ses olmadan başlat
      // isPreparingSpeech'i de false yap — yoksa kullanıcı sonraki mesajı gönderemiyor
      const forceStartTimer = window.setTimeout(() => {
        if (didStartTyping) return;
        window.clearTimeout(leadInTimer);
        pendingSpeechReadyRef.current = null;
        setIsPreparingSpeech(false);
        startSyncedTyping();
      }, 1800);

      void playTtsAudio(assistantMessage, {
        requireGestureFallback: true,
        onReady: (durationMs) => {
          if (didStartTyping) return;
          window.clearTimeout(forceStartTimer);
          pendingSpeechReadyRef.current = { durationMs };
          window.setTimeout(() => {
            if (didStartTyping) return;
            pendingSpeechReadyRef.current = null;
            window.clearTimeout(leadInTimer);
            startSyncedTyping(durationMs);
          }, 200);
        },
      });
    } else {
      startSyncedTyping(estimateSpeechDurationMs(assistantMessage));
    }
  };

  const tryLiveAssistantMessage = async (
    text: string,
    requestContext: Record<string, any>,
    historyForRequest: TutorMessage[],
  ) => {
    if (!voiceEnabledRef.current) return false;
    // Kullanıcı TTS modelini seçtiyse Live mod hiç denenmesin — direkt classic + TTS
    if (audioModeRef.current !== "live") return false;

    let liveConnection = liveConnectionRef.current;
    const requestedContextKey = buildAITeacherLiveContextKey(requestContext);
    if (
      liveConnection &&
      (liveConnection.contextKey !== requestedContextKey || liveConnection.socket.readyState !== WebSocket.OPEN)
    ) {
      closeLiveConnection();
      liveConnection = null;
    }

    let clientContent: AITeacherLiveSession["clientContent"] = buildAITeacherLiveClientContent(text);
    let nextContext = requestContext || resolvedContext;

    if (liveConnection?.socket.readyState === WebSocket.OPEN) {
      // Backend'in enriched verisini (gainPlan, pdfs vb.) koruyalım AMA güncel frontend
      // state'i (currentGainIndex vb.) backend snapshot'ını ezsin.
      nextContext = {
        ...(liveConnection.session.context || {}),
        ...(requestContext || {}),
        relatedMaterials: {
          ...((liveConnection.session.context as any)?.relatedMaterials || {}),
          ...((requestContext as any)?.relatedMaterials || {}),
        },
      };
      clientContent = buildAITeacherLiveClientContent(text);
    } else {
      let openingSocket: WebSocket | null = null;
      try {
        const session = await ApiService.createAITeacherLiveSession({
          message: text,
          context: requestContext,
          history: historyForRequest,
        });
        const socket = new WebSocket(session.websocketUrl);
        openingSocket = socket;
        const connection: AITeacherLiveConnection = {
          session,
          socket,
          contextKey: buildAITeacherLiveContextKey(session.context || requestContext),
        };

        await new Promise<void>((resolve, reject) => {
          let settled = false;
          let timeoutId: number | null = null;

          const finish = (error?: Error) => {
            if (settled) return;
            settled = true;
            if (timeoutId !== null) window.clearTimeout(timeoutId);
            if (error) reject(error);
            else resolve();
          };

          timeoutId = window.setTimeout(
            () => finish(new Error("AI öğretmen Live oturumu zamanında hazırlanamadı.")),
            15000,
          );

          socket.onopen = () => {
            socket.send(JSON.stringify({ setup: session.setup || {} }));
          };

          socket.onmessage = async (event) => {
            try {
              const rawData = typeof event.data === "string"
                ? event.data
                : event.data instanceof Blob
                  ? await event.data.text()
                  : String(event.data || "{}");
              const response = JSON.parse(rawData || "{}");
              if (response.error) {
                finish(new Error(response.error.message || "Gemini Live API hata döndürdü."));
                return;
              }
              if (response.setupComplete) finish();
            } catch (error: any) {
              finish(error);
            }
          };

          socket.onerror = () => finish(new Error("Gemini Live WebSocket bağlantısı başarısız oldu."));
          socket.onclose = (ev) => finish(new Error(`Gemini Live WebSocket bağlantısı hazırlık sırasında kapandı (code=${ev.code} reason=${ev.reason || 'none'}).`));
        });

        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = () => {
          if (liveConnectionRef.current?.socket === socket) {
            liveConnectionRef.current = null;
          }
        };

        liveConnectionRef.current = connection;
        liveConnection = connection;
        // Backend session.context'inde gainPlan vb. enriched data var — kaybetmeyelim.
        // requestContext frontend state'idir (currentGainIndex vb.); her ikisini de tutalım.
        nextContext = {
          ...(session.context || {}),
          ...(requestContext || {}),
          relatedMaterials: {
            ...((session.context as any)?.relatedMaterials || {}),
            ...((requestContext as any)?.relatedMaterials || {}),
          },
        };
        clientContent = session.clientContent;
        openingSocket = null;
      } catch (error: any) {
        const socketToClose = openingSocket;
        if (socketToClose && (socketToClose.readyState === WebSocket.OPEN || socketToClose.readyState === WebSocket.CONNECTING)) {
          socketToClose.close();
        }
        closeLiveConnection();
        console.warn("AI öğretmen Live oturumu hazırlanamadı:", error?.message || error);
        return false;
      }
    }

    if (!liveConnection) return false;

    const assistantMessageId = createMessageId();
    const requestId = speechRequestIdRef.current + 1;
    const socket = liveConnection.socket;
    let finalText = "";
    let displayedLiveText = "";
    let liveTypingTarget = "";
    let receivedAnyLiveData = false;
    let streamStarted = false;
    let sentClientContent = false;
    let liveFirstDataTimeoutId: number | null = null;
    let liveTypingTimerId: number | null = null;
    let liveTypingDoneResolve: (() => void) | null = null;
    const liveToolCalls: AITutorToolCall[] = [];

    const clearLiveFirstDataTimeout = () => {
      if (liveFirstDataTimeoutId === null) return;
      window.clearTimeout(liveFirstDataTimeoutId);
      liveFirstDataTimeoutId = null;
    };

    const markLiveDataReceived = () => {
      receivedAnyLiveData = true;
      clearLiveFirstDataTimeout();
    };

    const clearLiveTypingTimer = () => {
      if (liveTypingTimerId === null) return;
      window.clearTimeout(liveTypingTimerId);
      liveTypingTimerId = null;
    };

    const resolveLiveTypingIfComplete = () => {
      if (displayedLiveText.length < liveTypingTarget.length || !liveTypingDoneResolve) return;
      liveTypingDoneResolve();
      liveTypingDoneResolve = null;
    };

    const scheduleLiveTyping = () => {
      if (liveTypingTimerId !== null) return;

      if (!liveTypingTarget.startsWith(displayedLiveText)) {
        displayedLiveText = "";
      }

      if (displayedLiveText.length >= liveTypingTarget.length) {
        resolveLiveTypingIfComplete();
        return;
      }

      const nextIndex = displayedLiveText.length;
      const nextChar = liveTypingTarget[nextIndex] || "";
      displayedLiveText = liveTypingTarget.slice(0, nextIndex + 1);
      setMessages((prev) =>
        prev.map((messageItem) =>
          messageItem.id === assistantMessageId ? { ...messageItem, content: displayedLiveText } : messageItem,
        ),
      );

      const pauseMs = getTypingPauseAfterChar(liveTypingTarget, nextIndex);
      const liveCharacterDelayMs = Math.min(
        AI_TUTOR_TYPING_SETTINGS.maxCharacterDelayMs,
        Math.max(AI_TUTOR_TYPING_SETTINGS.minCharacterDelayMs, AI_TUTOR_TYPING_SETTINGS.fallbackMsPerCharacter),
      );
      const nextDelayMs = /\s/.test(nextChar)
        ? AI_TUTOR_TYPING_SETTINGS.spaceDelayMs
        : liveCharacterDelayMs + pauseMs;

      liveTypingTimerId = window.setTimeout(() => {
        liveTypingTimerId = null;
        scheduleLiveTyping();
      }, nextDelayMs);
    };

    const waitForLiveTypingComplete = () => {
      if (displayedLiveText.length >= liveTypingTarget.length) return Promise.resolve();

      return new Promise<void>((resolve) => {
        liveTypingDoneResolve = resolve;
        scheduleLiveTyping();
      });
    };

    const updateLiveText = (fragment: string) => {
      const nextText = appendLiveTextFragment(finalText, fragment);
      if (nextText === finalText) return;

      finalText = nextText;
      if (isInternalMetaAssistantText(nextText)) {
        markLiveDataReceived();
        return;
      }

      liveTypingTarget = nextText;
      markLiveDataReceived();
      scheduleLiveTyping();
    };

    const liveFlushTyping = () => {
      clearLiveTypingTimer();
      const nextText = liveTypingTarget || finalText || displayedLiveText;
      displayedLiveText = nextText;
      setMessages((prev) =>
        prev.map((messageItem) =>
          messageItem.id === assistantMessageId ? { ...messageItem, content: nextText } : messageItem,
        ),
      );
      resolveLiveTypingIfComplete();
      setIsTypingMessage(false);
    };
    const liveCancelTyping = () => {
      clearLiveTypingTimer();
      resolveLiveTypingIfComplete();
    };
    typingFlushRef.current = liveFlushTyping;
    typingCancelRef.current = liveCancelTyping;

    const finishStreamPlayback = () => {
      const audioContext = streamAudioContextRef.current;
      const remainingMs = audioContext
        ? Math.max(0, (streamPlaybackTimeRef.current - audioContext.currentTime) * 1000)
        : 0;

      window.setTimeout(() => {
        if (requestId !== speechRequestIdRef.current) return;
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
      }, remainingMs + 150);
    };

    const sendClientContent = () => {
      if (sentClientContent || !socket || socket.readyState !== WebSocket.OPEN) return;
      sentClientContent = true;
      socket.send(JSON.stringify({ clientContent }));
    };

    const handleLiveToolCall = (response: any) => {
      const functionCalls = response?.toolCall?.functionCalls || response?.serverContent?.toolCall?.functionCalls || [];
      if (!Array.isArray(functionCalls) || !functionCalls.length || !socket || socket.readyState !== WebSocket.OPEN) return;

      const mappedCalls = functionCalls
        .filter((call: any) => call?.name)
        .map((call: any, index: number) => ({
          id: call.id || `live-tool-${liveToolCalls.length + index}`,
          name: call.name as TutorToolName,
          arguments: call.args || {},
        }));

      liveToolCalls.push(...mappedCalls);
      markLiveDataReceived();
      applyToolCalls(mappedCalls, nextContext);

      socket.send(JSON.stringify({
        toolResponse: {
          functionResponses: mappedCalls.map((call) => ({
            id: call.id,
            name: call.name,
            response: { ok: true },
          })),
        },
      }));
    };

    try {
      setResolvedContext(mergeServerContext(nextContext));
      setIsPreparingSpeech(true);
      setSpeechError("");
      setSpeechNeedsGesture(false);
      setIsSpeaking(false);
      setIsTypingMessage(true);
      setLastAssistantMessage("");
      stopCurrentAudio();
      speechRequestIdRef.current = requestId;
      getStreamAudioContext(24000);
      setMessages((prev) => [...prev, { id: assistantMessageId, role: "assistant", content: "" }]);

      await new Promise<void>((resolve, reject) => {
        let settled = false;
        let timeoutId: number | null = null;
        let turnCompleteGraceTimeoutId: number | null = null;
        const finish = (error?: Error) => {
          if (settled) return;
          settled = true;
          clearLiveFirstDataTimeout();
          if (timeoutId !== null) window.clearTimeout(timeoutId);
          if (turnCompleteGraceTimeoutId !== null) window.clearTimeout(turnCompleteGraceTimeoutId);
          if (error) reject(error);
          else resolve();
        };
        const finishAfterTurnComplete = () => {
          if (turnCompleteGraceTimeoutId !== null) window.clearTimeout(turnCompleteGraceTimeoutId);
          turnCompleteGraceTimeoutId = window.setTimeout(() => finish(), AI_TUTOR_LIVE_TURN_COMPLETE_GRACE_MS);
        };
        timeoutId = window.setTimeout(() => finish(new Error("AI öğretmen Live yanıtı zaman aşımına uğradı.")), 45000);
        liveFirstDataTimeoutId = window.setTimeout(
          () => finish(new Error("AI öğretmen Live ilk ses/metin parçasını zamanında göndermedi.")),
          AI_TUTOR_LIVE_FIRST_DATA_TIMEOUT_MS,
        );
        socket.onmessage = async (event) => {
          try {
            const rawData = typeof event.data === "string"
              ? event.data
              : event.data instanceof Blob
                ? await event.data.text()
                : String(event.data || "{}");
            const response = JSON.parse(rawData || "{}");
            if (response.error) {
              finish(new Error(response.error.message || "Gemini Live API hata döndürdü."));
              return;
            }

            handleLiveToolCall(response);

            const modelParts = response.serverContent?.modelTurn?.parts;
            if (Array.isArray(modelParts)) {
              modelParts.forEach((part: any) => {
                if (typeof part?.text === "string") updateLiveText(part.text);

                const inlineData = part?.inlineData;
                if (inlineData?.data) {
                  const chunk = base64ToUint8Array(inlineData.data);
                  const sampleRate = getSampleRateFromMimeType(inlineData.mimeType || "audio/L16;rate=24000");
                  const queuedDurationMs = voiceEnabledRef.current ? queuePcmAudioChunk(chunk, sampleRate) : 0;
                  markLiveDataReceived();
                  if (queuedDurationMs > 0) {
                    streamStarted = true;
                    setIsPreparingSpeech(false);
                    setIsSpeaking(true);
                  }
                }
              });
            }

            const transcriptText =
              response.serverContent?.outputTranscription?.text ||
              response.outputTranscription?.text ||
              response.serverContent?.inputTranscription?.text ||
              "";
            if (transcriptText) updateLiveText(transcriptText);

            if (response.serverContent?.turnComplete) {
              finishAfterTurnComplete();
            }
          } catch (error) {
            finish(error as Error);
          }
        };

        socket.onerror = () => finish(new Error("Gemini Live WebSocket bağlantısı başarısız oldu."));
        socket.onclose = (ev) => {
          if (liveConnectionRef.current?.socket === socket) {
            liveConnectionRef.current = null;
          }
          if (receivedAnyLiveData) {
            finish();
            return;
          }
          finish(new Error(`Gemini Live WebSocket bağlantısı yanıt tamamlanmadan kapandı (code=${ev.code} reason=${ev.reason || 'none'}).`));
        };

        sendClientContent();
      });

      clearLiveFirstDataTimeout();
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = () => {
        if (liveConnectionRef.current?.socket === socket) {
          liveConnectionRef.current = null;
        }
      };
      finishStreamPlayback();
      liveTypingTarget = finalText;

      const assistantPayload = extractTextualToolCallsFromMessage(finalText || "Tamam, buradan devam edelim.");
      const assistantMessage = assistantPayload.message || "Tamam, buradan devam edelim.";
      if (isInternalMetaAssistantText(assistantMessage)) {
        setMessages((prev) => prev.filter((messageItem) => messageItem.id !== assistantMessageId));
        setIsTypingMessage(false);
        setIsPreparingSpeech(false);
        setIsSpeaking(false);
        stopCurrentAudio();
        if (liveConnectionRef.current?.socket === socket) {
          closeLiveConnection();
        }
        return false;
      }

      if (isLikelyIncompleteAssistantText(assistantMessage)) {
        setMessages((prev) => prev.filter((messageItem) => messageItem.id !== assistantMessageId));
        setIsTypingMessage(false);
        setIsPreparingSpeech(false);
        setIsSpeaking(false);
        stopCurrentAudio();
        if (liveConnectionRef.current?.socket === socket) {
          closeLiveConnection();
        }
        return false;
      }

      const mergedToolCalls = enhanceToolCallsForAssistantMessage(
        assistantMessage,
        [...liveToolCalls, ...assistantPayload.toolCalls],
        nextContext,
      );
      const pendingToolCalls = mergedToolCalls.filter(
        (toolCall) => !liveToolCalls.some((liveToolCall) => liveToolCall.id === toolCall.id && liveToolCall.name === toolCall.name),
      );
      applyToolCalls(pendingToolCalls, nextContext);
      await waitForLiveTypingComplete();

      setLastAssistantMessage(assistantMessage);
      setMessages((prev) =>
        prev.map((messageItem) =>
          messageItem.id === assistantMessageId ? { ...messageItem, content: assistantMessage } : messageItem,
        ),
      );
      setIsTypingMessage(false);
      if (typingFlushRef.current === liveFlushTyping) {
        typingFlushRef.current = null;
        typingCancelRef.current = null;
      }
      setIsLoading(false);

      if (!streamStarted) setIsPreparingSpeech(false);
      return true;
    } catch (error: any) {
      clearLiveFirstDataTimeout();
      clearLiveTypingTimer();
      if (typingFlushRef.current === liveFlushTyping) {
        typingFlushRef.current = null;
        typingCancelRef.current = null;
      }
      if (liveConnectionRef.current?.socket === socket) {
        closeLiveConnection();
      }
      setIsTypingMessage(false);
      setIsPreparingSpeech(false);
      setIsSpeaking(false);

      if (voiceCancelRequestedRef.current) {
        if (finalText || displayedLiveText) {
          const interruptedText = finalText || displayedLiveText;
          setMessages((prev) =>
            prev.map((messageItem) =>
              messageItem.id === assistantMessageId ? { ...messageItem, content: interruptedText } : messageItem,
            ),
          );
          setLastAssistantMessage(interruptedText);
        } else {
          setMessages((prev) => prev.filter((messageItem) => messageItem.id !== assistantMessageId));
        }
        voiceCancelRequestedRef.current = false;
        return true;
      }

      if (receivedAnyLiveData) {
        const fallbackMessage = finalText || error?.message || "AI öğretmen Live yanıtı tamamlanamadı.";
        if (isInternalMetaAssistantText(fallbackMessage)) {
          setMessages((prev) => prev.filter((messageItem) => messageItem.id !== assistantMessageId));
          stopCurrentAudio();
          return false;
        }

        setMessages((prev) =>
          prev.map((messageItem) =>
            messageItem.id === assistantMessageId ? { ...messageItem, content: fallbackMessage } : messageItem,
          ),
        );
        setLastAssistantMessage(fallbackMessage);
        return true;
      }

      setMessages((prev) => prev.filter((messageItem) => messageItem.id !== assistantMessageId));
      console.warn("AI öğretmen Live akışı başarısız, klasik akış kullanılacak:", error);
      return false;
    }
  };

  const isLiveModeActive = () => voiceEnabledRef.current && audioModeRef.current === "live";

  const tryLiveWithRetry = async (
    text: string,
    requestContext: Record<string, any>,
    historyForRequest: TutorMessage[],
  ) => {
    const first = await tryLiveAssistantMessage(text, requestContext, historyForRequest);
    if (first) return true;
    if (!isLiveModeActive()) return false;
    await new Promise((resolve) => window.setTimeout(resolve, 600));
    return tryLiveAssistantMessage(text, requestContext, historyForRequest);
  };

  const sendHiddenMessage = async (text: string, contextOverride?: Record<string, any>) => {
    const hiddenText = text.trim();
    if (!hiddenText) return;
    const requestContext = contextOverride || getRequestContext();

    setIsLoading(true);
    try {
      // Live mod aktifken Live'da kal; bir retry sonrası hâlâ başarısızsa /chat'e düşme — hata göster
      const liveHandled = await tryLiveWithRetry(hiddenText, requestContext, messages);
      if (liveHandled) return;
      if (isLiveModeActive()) {
        setMessages((prev) => [...prev, { id: createMessageId(), role: "assistant", content: "Sesli oturum şu an açılamadı. Sıfırla ya da birkaç saniye sonra tekrar dene.", transient: true }]);
        return;
      }

      const response = await ApiService.sendAITeacherMessage({
        message: hiddenText,
        context: requestContext,
        history: messages.filter((m) => m.content && !m.transient).slice(-15),
      });
      const nextContext = mergeServerContext(response.context || resolvedContext);
      const assistantPayload = extractTextualToolCallsFromMessage(response.message || "");
      const assistantMessage = getSafeAssistantMessage(
        assistantPayload.message || "Test sonucunu birlikte değerlendirelim.",
        "Burada takıldığın noktayı birlikte toparlayalım. Önce ana fikri netleştirip sonra küçük bir örnek üzerinden ilerleyelim.",
      );

      const toolCalls = [...(response.toolCalls || []), ...assistantPayload.toolCalls];
      setResolvedContext(nextContext);
      applyToolCalls(enhanceToolCallsForAssistantMessage(assistantMessage, toolCalls, nextContext), nextContext);

      setIsLoading(false);
      await appendAssistantMessage(assistantMessage);
    } catch (error: any) {
      setMessages((prev) => [...prev, { role: "assistant", content: error?.message || "AI öğretmene ulaşılamadı." }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Geçişler artık AI'nın move_to_next_gain tool çağrısıyla yapılıyor

  const sendMessage = async (text: string, options: { fromVoice?: boolean; contextOverride?: Record<string, any> } = {}) => {
    if (isLoading || isTypingMessage || isPreparingSpeech) return;

    const trimmed = text.trim();
    const nextMessages = trimmed ? [...messages, { id: createMessageId(), role: "user" as const, content: trimmed }] : messages;

    if (trimmed) {
      lastUserMessageRef.current = trimmed;
      if (!options.fromVoice && voiceConversationActiveRef.current) {
        setVoiceConversationActive(false);
        voiceConversationActiveRef.current = false;
        setVoiceBubbleActive(false);
        cleanupRecordingMonitor();
        recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        recorderRef.current = null;
        setIsRecording(false);
      }
      setMessages(nextMessages);
      setInput("");
    }

    setIsLoading(true);

    // 45 saniye içinde tamamlanmazsa tüm loading state'lerini sıfırla
    const safetyTimer = window.setTimeout(() => {
      setIsLoading(false);
      setIsTypingMessage(false);
      setIsPreparingSpeech(false);
      setIsSpeaking(false);
    }, 45000);

    try {
      // History = ÖNCEKİ mesajlar (messages, nextMessages DEĞİL) — güncel mesaj ayrıca `message`
      // alanında gidiyor; nextMessages kullanılırsa mesaj history'de de yer alıp AI'a 2 kez gider
      // ("7" -> "77"). transient/boş mesajlar da prompt'u kirletmesin diye filtrelenir.
      const trimmedHistory = messages.filter((m) => m.content && !m.transient).slice(-15);

      const effectiveRequestContext = options.contextOverride
        ? { ...getRequestContext(), ...options.contextOverride }
        : getRequestContext();

      // Live mod aktifken Live'da kal; bir retry sonrası hâlâ başarısızsa /chat'e düşme — hata göster
      const liveHandled = await tryLiveWithRetry(trimmed, effectiveRequestContext, trimmedHistory);
      if (liveHandled) return;
      if (isLiveModeActive()) {
        setMessages((prev) => [...prev, { id: createMessageId(), role: "assistant", content: "Sesli oturum şu an açılamadı. Sıfırla ya da birkaç saniye sonra tekrar dene.", transient: true }]);
        return;
      }

      const response = await ApiService.sendAITeacherMessage({
        message: trimmed,
        context: effectiveRequestContext,
        history: trimmedHistory,
      });
      const nextContext = mergeServerContext(response.context || resolvedContext);
      setResolvedContext(nextContext);

      const assistantPayload = extractTextualToolCallsFromMessage(response.message || "");
      const assistantMessage = getSafeAssistantMessage(
        assistantPayload.message || "Devam edelim. Bu konuda aklına gelen ilk şeyi söyle.",
        "Eksik olduğun bu kazanımda önce ana fikri netleştirelim. Konuyu küçük bir örnekle bağlayıp sonra sana kısa bir kontrol sorusu soracağım.",
      );

      const toolCalls = [...(response.toolCalls || []), ...assistantPayload.toolCalls];
      applyToolCalls(enhanceToolCallsForAssistantMessage(assistantMessage, toolCalls, nextContext), nextContext);
      setIsLoading(false);
      await appendAssistantMessage(assistantMessage);
    } catch (error: any) {
      const errorMessage = error?.message || "AI öğretmene ulaşılamadı.";
      setMessages((prev) => [...prev, { role: "assistant", content: errorMessage }]);
    } finally {
      window.clearTimeout(safetyTimer);
      setIsLoading(false);
    }
  };

  const startRecording = async () => {
    if (isRecording || isLoading || isPreparingSpeech) return;

    voiceCancelRequestedRef.current = false;
    setVoiceConversationActive(true);
    voiceConversationActiveRef.current = true;
    setVoiceBubbleActive(true);
    setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
    try {
      // Echo cancellation şart — AI kendi sesini duymasın
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorderRef.current = recorder;
      recordingStreamRef.current = stream;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = async () => {
        cleanupRecordingMonitor();
        stream.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        recorderRef.current = null;
        setIsRecording(false);

        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (!voiceConversationActiveRef.current) return;
        if (audioBlob.size === 0) return;

        setIsLoading(true);
        try {
          const transcription = await ApiService.transcribeAudio(audioBlob, "tr");
          const text = String(transcription.text || "").trim();
          setInput(text);
          setIsLoading(false);
          if (text) {
            await sendMessage(text, { fromVoice: true });
          }
        } catch (error: any) {
          setSpeechError(error?.message || "Ses metne çevrilemedi.");
        } finally {
          setIsLoading(false);
        }
      };

      recorder.start();
      startRecordingSilenceMonitor(stream);
      setIsRecording(true);
    } catch (error: any) {
      setSpeechError(error?.message || "Mikrofon açılamadı.");
      setVoiceBubbleActive(false);
      setVoiceConversationActive(false);
      voiceConversationActiveRef.current = false;
      cleanupRecordingMonitor();
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
      recorderRef.current = null;
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    cleanupRecordingMonitor();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
      return;
    }

    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordingStreamRef.current = null;
    recorderRef.current = null;
    setIsRecording(false);
  };
  stopRecordingRef.current = stopRecording;

  useEffect(() => {
    if (
      !voiceConversationActive ||
      !voiceEnabled ||
      isRecording ||
      isLoading ||
      isTypingMessage ||
      isPreparingSpeech ||
      isSpeaking
    ) {
      return;
    }

    const listenAgainTimer = window.setTimeout(() => {
      if (!voiceConversationActiveRef.current || recorderRef.current || recordingStreamRef.current) return;
      void startRecording();
    }, 420);

    return () => window.clearTimeout(listenAgainTimer);
  }, [voiceConversationActive, voiceEnabled, isRecording, isLoading, isTypingMessage, isPreparingSpeech, isSpeaking]);

  const cancelVoiceConversation = () => {
    voiceCancelRequestedRef.current = true;
    flushCurrentAssistantTyping();
    setVoiceConversationActive(false);
    voiceConversationActiveRef.current = false;
    setVoiceBubbleActive(false);
    setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
    setIsRecording(false);

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
      recorderRef.current = null;
    }

    cleanupRecordingMonitor();
    // NOT: stopCurrentAudio() ve closeLiveConnection() artık çağrılmıyor —
    // AI konuşuyorsa konuşmasını bitirsin, Live bağlantı bir sonraki turda da çalışsın
  };

  const handleClose = () => {
    voiceCancelRequestedRef.current = true;
    flushCurrentAssistantTyping();
    stopCurrentAudio();
    closeLiveConnection();
    onClose();
  };

  const replayLastAssistantMessage = () => {
    if (!lastAssistantMessage) return;

    setVoiceEnabled(true);
    setSpeechNeedsGesture(false);
    void playTtsAudio(lastAssistantMessage, { requireGestureFallback: false });
  };

  const handlePracticeTestComplete = (summary: PracticeTestResultSummary) => {
    // Test bittikten sonra Live mod ve ses tam çalışsın
    try {
      getStreamAudioContext(24000);
    } catch {}

    // Sesi SADECE test öncesi öğrenci sesli konuşuyorsa geri aç — text kullanıcıyı ses moduna zorlama
    // (aksi halde test bitince ekranda beklenmedik ses balonu çıkıp interaktif akışı bozuyordu).
    if (voiceActiveBeforeTestRef.current) {
      voiceConversationActiveRef.current = true;
      setVoiceConversationActive(true);
      setVoiceBubbleActive(true);
    }

    const completedGainId = activeMaterial.type === "test" ? activeMaterial.gainId : "";
    const passed = summary.wrong === 0 && summary.blank === 0;
    // Reopen guard SADECE başarıda — başarısızsa aynı kazanıma pekiştirme testi tekrar açılabilsin
    completedTestGainIdRef.current = passed ? (completedGainId || null) : null;
    // Başarısızsa bu kazanımda İLERLEME KİLİDİ; başarıyla geçilince kilit kalkar
    lastFailedTestGainIdRef.current = passed ? null : (completedGainId || null);

    // DETERMİNİSTİK İLERLEME: yalnızca yanlışsız+boşsuz geçildiyse sonraki kazanıma otomatik geç
    if (passed) {
      const gp = Array.isArray(resolvedContext.relatedMaterials?.gainPlan) ? resolvedContext.relatedMaterials.gainPlan : [];
      const idx = gp.length ? Math.min(Math.max(0, Number(resolvedContext.currentGainIndex) || 0), gp.length - 1) : 0;
      if (gp.length && idx < gp.length - 1 && Date.now() - lastGainAdvanceAtRef.current > 10000) {
        const nextIndex = idx + 1;
        lastGainAdvanceAtRef.current = Date.now(); // çift-ilerleme guard
        gainIndexRef.current = nextIndex;
        setResolvedContext((prev) => ({ ...prev, currentGainIndex: nextIndex }));
        completedTestGainIdRef.current = null; // yeni kazanıma geçildi → test tekrar açılabilir
        openTestQuestionsRef.current = []; // eski kazanımın açık test soruları context'ten düşsün
        // Test-geçildi otomatik ilerlemede move_to_next_gain TOOL'u çağrılmıyor (index doğrudan set),
        // bu yüzden Live grounding ESKİ kazanımda donuk kalıp mesajı tekrar ediyordu → bağlantıyı kapat,
        // sonraki tur (tebrik + yeni kazanım tanıtımı) YENİ kazanım grounding'iyle taze kurulur.
        closeLiveConnection();
        const spId = resolvedContext.sessionPlanId || context.sessionPlanId;
        if (spId) void ApiService.updateSessionPlanProgress(String(spId), { currentGainIndex: nextIndex }).catch(() => {});
      }
    }

    // isPracticeResult: bu bir test SONUCU mesajı → backend otomatik test enjeksiyonundan MUAF.
    // Fix B: getRequestContext() bu render'da BAYAT currentGainIndex okuyor (setResolvedContext henüz
    // flush olmadı); otoriter gainIndexRef.current'i açıkça geçir ki geçince "tebrik + yeni kazanımı
    // tanıt" mesajı YENİ kazanıma grounded olsun (eskiyi yeniden anlatmasın).
    const resultContext = { ...getRequestContext(), currentGainIndex: gainIndexRef.current, isPracticeResult: true };

    if (passed) {
      void sendHiddenMessage(
        `Pekiştirme testini YANLIŞSIZ tamamladım. Beni kısaca tebrik et. Sistem otomatik olarak bir sonraki kazanıma geçmiş olabilir; öyleyse yeni kazanımı kısa ve doğal bir cümleyle tanıt. Aynı testi tekrar AÇMA.`,
        resultContext,
      );
    } else {
      const wrongText = summary.wrongQuestions
        .slice(0, 5)
        .map((item) => (
          `${item.index}. soru: öğrenci ${item.selectedAnswer || "boş"} işaretledi${item.selectedAnswerText ? ` (${item.selectedAnswerText})` : ""}, ` +
          `doğru cevap ${item.correctAnswer || "belirsiz"}${item.correctAnswerText ? ` (${item.correctAnswerText})` : ""}. Soru: ${item.text}`
        ))
        .join("\n");

      void sendHiddenMessage(
        `Pekiştirme testini bitirdim: ${summary.correct} doğru, ${summary.wrong} yanlış, ${summary.blank} boş. Sol tarafta yanlış sorular inceleme modunda; her yanlış sorunun (varsa) çözümlü videosu da orada. ` +
        `ŞİMDİ şunu yap: Yanlış yaptığım soruları TEK TEK ele al — her biri için hangi yanlış seçeneğe neden kanmış olabileceğimi ve doğru cevaba adım adım nasıl gidileceğini net açıkla; çözümlü videosu olan sorularda videoyu izlememi öner. Gerekiyorsa kazanımı farklı bir açıdan pekiştir. ` +
        `ŞİMDİ YENİ TEST AÇMA (open_practice_test çağırma) ve bir sonraki kazanıma GEÇME (move_to_next_gain çağırma). Yanlışları anladıktan sonra ekrandaki "Yanlışları tekrar çöz" butonuyla tekrar deneyeceğim.\n${wrongText}`,
        resultContext,
      );
    }
  };

  if (!isOpen) return null;

  const shellLeftClass =
    typeof window !== "undefined" && window.innerWidth < 768
      ? "left-0"
      : sidebarExpanded
        ? "md:left-64 left-0"
        : "md:left-[50px] left-0";
  const inputBusy = isLoading || isTypingMessage || isPreparingSpeech;
  const recordingDisabled = (inputBusy || isSpeaking) && !isRecording;
  const voiceBubbleExpanded = voiceBubbleActive || isRecording || isSpeaking || isPreparingSpeech;
  const voiceBubbleAnimating = isRecording || isSpeaking;
  const handleVoiceBubbleClick = () => {
    if (isRecording) {
      stopRecording();
      return;
    }

    void startRecording();
  };

  const handleResetConversation = async () => {
    const sessionPlanId = context?.sessionPlanId || resolvedContext?.sessionPlanId;
    if (!sessionPlanId) return;
    if (!window.confirm("Bu konuşmayı sıfırlamak istediğine emin misin? Tüm mesajlar silinecek ve 1. kazanımdan yeniden başlayacaksın.")) return;

    try {
      await ApiService.resetSessionPlan(String(sessionPlanId));
    } catch {
      // Backend'e ulaşılamazsa yine de local state'i temizle — kullanıcı tekrar deneyebilir
    }

    stopCurrentAudio();
    closeLiveConnection();
    cleanupRecordingMonitor();
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordingStreamRef.current = null;
    recorderRef.current = null;
    setIsRecording(false);
    setIsSpeaking(false);
    setIsPreparingSpeech(false);
    setIsTypingMessage(false);
    setLastAssistantMessage("");
    setInput("");
    setMessages([]);
    setActiveMaterial({ type: "empty" });
    gainIndexRef.current = 0;
    setResolvedContext((prev) => ({ ...prev, currentGainIndex: 0 }));
    void sendMessage("", { contextOverride: { currentGainIndex: 0 } });
  };

  return (
    <div className={`fixed top-[58px] right-0 bottom-0 ${shellLeftClass} z-[70] bg-white border-t border-gray-200 shadow-2xl flex`}>
      <section className="flex-1 min-w-0 bg-white relative overflow-hidden">
        <Workspace
          material={activeMaterial}
          context={resolvedContext}
          onPracticeTestComplete={handlePracticeTestComplete}
          onMaterialAction={sendHiddenMessage}
        />
      </section>

      <aside className="w-[380px] max-w-[42vw] min-w-[340px] border-l border-gray-200 bg-gray-50 flex flex-col">
        <div className="px-4 py-3 bg-[#240d07] text-white flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm">AI Öğretmen Destek</div>
              <div className="text-[11px] text-white/70 truncate">
                {context.topicTitle || context.contentTitle || context.gainText || "Aktif konu"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const nextVoiceEnabled = !voiceEnabled;
                voiceEnabledRef.current = nextVoiceEnabled;
                setVoiceEnabled(nextVoiceEnabled);
                setSpeechNeedsGesture(false);
                if (!nextVoiceEnabled) {
                  setIsSpeaking(false);
                  setIsPreparingSpeech(false);
                  stopCurrentAudio();
                  // Voice kapanınca Live bağlantıyı da kapat
                  closeLiveConnection();
                } else {
                  // Voice tekrar açılınca AudioContext'i şimdiki gesture ile başlat
                  // (sonraki AI mesajının sesi çalabilsin diye)
                  try {
                    getStreamAudioContext(24000);
                  } catch {}
                }
              }}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${voiceEnabled
                ? "text-white"
                : "text-white/70"
                }`}
              title={voiceEnabled ? "Sesli okumayı kapat" : "Sesli okumayı aç"}
              aria-label={voiceEnabled ? "Sesli okumayı kapat" : "Sesli okumayı aç"}
            >
              {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center"
              aria-label="AI Öğretmen Destek kapat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.map((message, index) => {
            const isEmptyAssistant = message.role === "assistant" && !message.content.trim();
            if (isEmptyAssistant) {
              return (
                <ShinyStatusBubble
                  key={message.id || `${message.role}-${index}`}
                  text={isPreparingSpeech ? "Hazırlanıyor..." : "Düşünüyor..."}
                />
              );
            }

            return (
              <div key={message.id || `${message.role}-${index}`} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[88%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${message.role === "user"
                    ? "bg-orange-500 text-white rounded-tr-md"
                    : "bg-white text-gray-800 border border-gray-200 rounded-tl-md"
                    }`}
                >
                  <span className="whitespace-pre-wrap">{message.content}</span>
                  {message.role === "assistant" && isTypingMessage && index === messages.length - 1 && (
                    <span className="ml-0.5 inline-block h-4 w-0.5 translate-y-0.5 animate-pulse rounded-full bg-orange-400" />
                  )}
                </div>
              </div>
            );
          })}

          {isLoading && !isTypingMessage && !isPreparingSpeech && !messages.some((message) => message.role === "assistant" && !message.content.trim()) && (
            <ShinyStatusBubble text="Düşünüyor..." />
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 border-t border-gray-200 bg-white">
          {hasTopicGainPlan && (
            <div className="mb-2 rounded-[22px] border border-orange-100 bg-orange-50 px-3 py-2 shadow-sm">
              <button
                type="button"
                onClick={() => setIsGainCardExpanded((prev) => !prev)}
                className="flex w-full items-start justify-between gap-2 rounded-2xl text-left transition"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-extrabold leading-5 text-orange-700">
                    {currentGainIndex + 1}/{topicGainPlan.length} Eksik Kazanım
                  </div>
                  {!isGainCardExpanded && (
                    <div className="mt-1 break-words text-sm font-semibold leading-5 text-orange-950">
                      {activePlannedGain?.gainText || "Aktif kazanım"}
                    </div>
                  )}
                </div>
                <span className="mt-0.5 rounded-full p-1 text-gray-400">
                  {isGainCardExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </span>
              </button>
              {isGainCardExpanded && (
                <ul className="mt-2 space-y-1">
                  {topicGainPlan.map((gain: any, index: number) => {
                    const isActive = index === currentGainIndex;
                    return (
                      <li
                        key={gain.gainId || gain.userGainId || index}
                        className={`flex items-start gap-2 rounded-xl px-2 py-1.5 text-xs leading-5 ${
                          isActive
                            ? "bg-orange-100 text-orange-950 font-semibold"
                            : "text-orange-900/70"
                        }`}
                      >
                        <span className={`shrink-0 ${isActive ? "font-extrabold text-orange-700" : "text-orange-700/60"}`}>
                          {index + 1}/{topicGainPlan.length}
                        </span>
                        <span className="break-words">{gain.gainText || "Kazanım"}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
          {speechError && (
            <div className="mb-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
              {speechError}
            </div>
          )}
          {speechNeedsGesture && lastAssistantMessage && (
            <button
              onClick={replayLastAssistantMessage}
              className="mb-2 w-full rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-700 flex items-center justify-center gap-2 hover:bg-orange-100"
            >
              <Volume2 className="w-3.5 h-3.5" />
              Sesi başlat
            </button>
          )}
          <div
            className={`relative flex items-center overflow-hidden transition-[gap,padding] duration-300 ease-out ${
              voiceBubbleExpanded ? "gap-0 pr-0" : "gap-3 pr-[60px]"
            }`}
          >
            <VoiceBubbleControl
              expanded={voiceBubbleExpanded}
              animating={voiceBubbleAnimating}
              disabled={recordingDisabled}
              level={voiceLevel}
              onClick={handleVoiceBubbleClick}
              onCancel={cancelVoiceConversation}
            />
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey && input.trim() && !inputBusy && !voiceBubbleExpanded) {
                  event.preventDefault();
                  void sendMessage(input);
                }
              }}
              disabled={inputBusy || voiceBubbleExpanded}
              tabIndex={voiceBubbleExpanded ? -1 : 0}
              placeholder="Konuş ya da yaz..."
              className={`min-w-0 h-12 rounded-2xl border border-gray-200 text-sm outline-none transition-all duration-300 ease-out focus:border-orange-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                voiceBubbleExpanded
                  ? "pointer-events-none w-0 flex-none border-transparent px-0 opacity-0"
                  : "flex-1 px-4 opacity-100"
              }`}
            />
            <button
              onClick={() => void sendMessage(input)}
              disabled={!input.trim() || inputBusy || voiceBubbleExpanded}
              tabIndex={voiceBubbleExpanded ? -1 : 0}
              className={`absolute right-0 top-0 h-12 w-12 rounded-2xl bg-orange-500 text-white flex items-center justify-center transition-all duration-300 ease-out disabled:opacity-50 hover:bg-orange-600 ${
                voiceBubbleExpanded
                  ? "pointer-events-none translate-x-[calc(100%+12px)] scale-95 opacity-0"
                  : "translate-x-0 scale-100 opacity-100"
              }`}
              title="Gönder"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
};

const Workspace = ({
  material,
  context,
  onPracticeTestComplete,
  onMaterialAction,
}: {
  material: ActiveMaterial;
  context: Record<string, any>;
  onPracticeTestComplete: (summary: PracticeTestResultSummary) => void;
  onMaterialAction: (message: string) => void;
}) => {
  if (material.type === "pdf") {
    return <PdfMaterialWorkspace material={material} context={context} onMaterialAction={onMaterialAction} />;
  }

  if (material.type === "video") {
    const startSeconds = timecodeToSeconds(material.startTime);
    const endSeconds = timecodeToSeconds(material.endTime);
    const resolvedVideoUrl = resolveTutorMediaUrl(material.videoUrl);
    const vimeoId = getVimeoId(resolvedVideoUrl);
    const youtubeId = getYoutubeId(resolvedVideoUrl);

    return (
      <div className="h-full bg-white flex flex-col">
        <div className="h-14 px-5 border-b border-gray-200 flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Video className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <span className="truncate">{material.title}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-100 rounded-full px-3 py-1 whitespace-nowrap">
              {material.startTime || "00:00:00"} - {material.endTime || "00:00:00"}
            </div>
            <button
              type="button"
              onClick={() => onMaterialAction("Videoyu izledim. Aynı videoyu tekrar açma; bu aralıkta anlatılanları eksiğimle ilişkilendir ve sıradaki uygun adımı öner.")}
              className="rounded-full bg-purple-600 px-3 py-1 text-xs font-bold text-white transition hover:bg-purple-700"
            >
              Videoyu izledim
            </button>
          </div>
        </div>

        <div className="bg-black">
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            {youtubeId ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1${startSeconds ? `&start=${startSeconds}` : ""}${endSeconds > startSeconds ? `&end=${endSeconds}` : ""}`}
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`ai-tutor-video-${youtubeId}`}
              />
            ) : vimeoId ? (
              <iframe
                src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1${startSeconds ? `#t=${startSeconds}s` : ""}`}
                className="absolute inset-0 h-full w-full"
                allow="autoplay; fullscreen"
                title={`ai-tutor-video-${vimeoId}`}
              />
            ) : resolvedVideoUrl ? (
              <TutorDirectVideoPlayer src={resolvedVideoUrl} startSeconds={startSeconds} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-sm font-medium text-white">
                Video URL bilgisi bulunamadı.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (material.type === "simulation") {
    return (
      <div className="h-full bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-3xl rounded-2xl border border-orange-200 bg-orange-50 p-8">
          <div className="flex items-center gap-3 text-lg font-bold text-orange-900 mb-6">
            <Shapes className="w-6 h-6" />
            Simülasyon Çalışma Alanı
          </div>
          <div className="grid grid-cols-3 gap-4">
            {["Gözlemle", "Değiştir", "Sonuç çıkar"].map((item, index) => (
              <div key={item} className="rounded-xl bg-white border border-orange-100 p-5 text-center">
                <div className="w-9 h-9 rounded-full bg-orange-500 text-white flex items-center justify-center mx-auto mb-3 font-bold">
                  {index + 1}
                </div>
                <div className="font-semibold text-orange-900">{item}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm leading-7 text-orange-800">
            Konu kimliği: {material.topicId || context.topicId || "aktif konu"}. AI öğretmenin sağdaki yönergelerine göre simülasyonu yorumla.
          </p>
        </div>
      </div>
    );
  }

  if (material.type === "test-loading") {
    return (
      <div className="h-full bg-white flex items-center justify-center p-8">
        <div className="rounded-2xl border border-orange-100 bg-orange-50 px-5 py-4 text-orange-800 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin" />
          Pekiştirme testi yükleniyor...
        </div>
      </div>
    );
  }

  if (material.type === "test-error") {
    return <BlankWorkspace title="Test bulunamadı" text={material.message} />;
  }

  if (material.type === "test") {
    return <PracticeTestWorkspace material={material} context={context} onComplete={onPracticeTestComplete} />;
  }

  return (
    <BlankWorkspace
      title={context.topicTitle || context.contentTitle || context.gainText || "AI Öğretmen Çalışma Alanı"}
      text="AI öğretmen konuşma sırasında gerektiğinde burada PDF, simülasyon veya test çalışma alanını açacak."
    />
  );
};

const normalizeQuestionOptions = (options: TutorQuestion["options"]) => {
  if (Array.isArray(options)) {
    return options.map((option, index) => {
      const label = String.fromCharCode(65 + index);
      if (typeof option === "string") return { label, text: option };
      return { label: option.label || label, text: option.text || option.value || "" };
    }).filter((option) => option.text);
  }

  return Object.entries(options || {}).map(([label, text]) => ({
    label,
    text: String(text || ""),
  })).filter((option) => option.text);
};

const getActivePracticeGainText = (context: Record<string, any>) => {
  const gainPlan = Array.isArray(context.relatedMaterials?.gainPlan) ? context.relatedMaterials.gainPlan : [];
  const activeIndex = gainPlan.length
    ? Math.min(Math.max(0, Number(context.currentGainIndex) || 0), gainPlan.length - 1)
    : 0;
  const activeGainText = gainPlan[activeIndex]?.gainText || context.gainText || context.topicTitle || context.contentTitle || "Aktif kazanım";
  return String(activeGainText).split("|")[0].replace(/\s+/g, " ").trim();
};

// Çözümlü video gömme — Vimeo / YouTube / mp4 (soru solutionVideoUrl'i).
const SolutionVideoEmbed = ({ url }: { url: string }) => {
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (vimeo) {
    return (
      <div className="relative w-full overflow-hidden rounded-xl bg-black" style={{ paddingTop: "56.25%" }}>
        <iframe className="absolute inset-0 h-full w-full" src={`https://player.vimeo.com/video/${vimeo[1]}`} title="Çözüm videosu" frameBorder={0} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  if (yt) {
    return (
      <div className="relative w-full overflow-hidden rounded-xl bg-black" style={{ paddingTop: "56.25%" }}>
        <iframe className="absolute inset-0 h-full w-full" src={`https://www.youtube-nocookie.com/embed/${yt[1]}?rel=0`} title="Çözüm videosu" frameBorder={0} allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  return <video src={url} controls className="w-full rounded-xl bg-black" />;
};

const PracticeTestWorkspace = ({
  material,
  context,
  onComplete,
}: {
  material: Extract<ActiveMaterial, { type: "test" }>;
  context: Record<string, any>;
  onComplete: (summary: PracticeTestResultSummary) => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  // Yanlışları tekrar çöz: yalnız yanlış alt-kümesiyle yeni tur (körü körüne aynı testi tekrar açma yerine)
  const [retryQuestions, setRetryQuestions] = useState<TutorQuestion[] | null>(null);
  const [solutionUrl, setSolutionUrl] = useState<string | null>(null);
  const questions = retryQuestions ?? material.questions;
  const question = questions[currentIndex];
  const options = normalizeQuestionOptions(question?.options);
  const questionKey = String(question?.id ?? currentIndex);
  const selectedAnswer = selectedAnswers[questionKey];
  const isLastQuestion = currentIndex >= questions.length - 1;
  const answeredCount = questions.filter((item, index) => selectedAnswers[String(item?.id ?? index)]).length;
  const isCorrectAnswer = (item: TutorQuestion, index: number) => {
    const answer = selectedAnswers[String(item?.id ?? index)];
    return Boolean(answer && item.correctAnswer && answer === item.correctAnswer);
  };
  const correctCount = questions.filter((item, index) => {
    return isCorrectAnswer(item, index);
  }).length;
  const wrongQuestions = questions
    .map((item, index) => ({
      item,
      index,
      selectedAnswer: selectedAnswers[String(item?.id ?? index)],
    }))
    .filter(({ item, selectedAnswer }) => selectedAnswer && item.correctAnswer && selectedAnswer !== item.correctAnswer)
    .map(({ item, index, selectedAnswer }) => {
      const itemOptions = normalizeQuestionOptions(item.options);
      const selectedOption = itemOptions.find((option) => option.label === selectedAnswer);
      const correctOption = itemOptions.find((option) => option.label === item.correctAnswer);

      return {
        index: index + 1,
        text: item.text || "Soru metni yok",
        selectedAnswer,
        selectedAnswerText: selectedOption?.text,
        correctAnswer: item.correctAnswer,
        correctAnswerText: correctOption?.text,
      };
    });
  const wrongCount = wrongQuestions.length;
  const blankCount = questions.length - answeredCount;
  const reviewQuestionIndexes = wrongQuestions.map((item) => item.index - 1);
  const currentReviewPosition = reviewQuestionIndexes.indexOf(currentIndex);
  const isReviewFirstQuestion = isReviewMode && currentReviewPosition <= 0;
  const isReviewLastQuestion = isReviewMode && currentReviewPosition >= reviewQuestionIndexes.length - 1;
  const activeGainText = getActivePracticeGainText(context);
  const questionText = String(question?.text || "").replace(/\s+/g, " ").trim();
  const questionMediaUrl = question?.mediaUrl ? resolveTutorMediaUrl(question.mediaUrl) : "";
  const hasQuestionBody = Boolean(questionText || questionMediaUrl);

  const completeTest = () => {
    onComplete({
      total: questions.length,
      answered: answeredCount,
      correct: correctCount,
      wrong: wrongCount,
      blank: blankCount,
      wrongQuestions,
    });

    if (wrongQuestions.length) {
      setIsReviewMode(true);
      setIsCompleted(false);
      setCurrentIndex(wrongQuestions[0].index - 1);
      return;
    }

    setIsCompleted(true);
  };

  if (isCompleted) {
    return (
      <div className="h-full bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-2xl rounded-2xl border border-green-200 bg-green-50 p-8 text-center">
          <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-600" />
          <h2 className="text-2xl font-bold text-green-950">Test Tamamlandı</h2>
          <p className="mt-3 text-lg font-semibold">
            <span className="text-green-600">{correctCount} doğru</span>
            <span className="text-green-900">, </span>
            <span className="text-red-600">{wrongCount} yanlış</span>
            <span className="text-green-900">, </span>
            <span className="text-gray-500">{blankCount} boş</span>
          </p>
          <button
            onClick={() => {
              setIsCompleted(false);
              setIsReviewMode(true);
              setCurrentIndex(reviewQuestionIndexes[0] ?? 0);
            }}
            className="mt-6 rounded-xl bg-orange-500 px-5 py-2 text-sm font-semibold text-white hover:bg-orange-600"
          >
            Soruları Gözden Geçir
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-[#f8fafc] flex flex-col">
      <div className="h-16 px-6 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-blue-950 font-bold">
            <CheckCircle2 className="w-5 h-5 text-blue-600" />
            Pekiştirme Testi
          </div>
          <div className="mt-0.5 truncate text-xs font-medium text-gray-500">
            {activeGainText}
          </div>
        </div>
        <div className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-sm font-bold text-blue-700">
          {currentIndex + 1} / {questions.length}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-4xl space-y-4">
          <div className={`rounded-2xl border px-4 py-3 text-sm leading-6 shadow-sm ${isReviewMode ? "border-amber-200 bg-amber-50 text-amber-900" : "border-blue-100 bg-blue-50 text-blue-900"}`}>
            {isReviewMode ? (
              <>
                <span className="font-semibold">Yanlış yaptığın soru açık.</span>{" "}
                Kırmızı seçtiğin cevap, yeşil doğru cevap. Sonucu AI Öğretmen sağ tarafta açıklıyor.
              </>
            ) : (
              "İlk soruda önce hangi kavramın ölçüldüğünü belirle, sonra seçenekleri tek tek ele."
            )}
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-extrabold text-orange-700">
                Soru {currentIndex + 1}
              </span>
              <span className="min-w-0 max-w-full truncate rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                {activeGainText}
              </span>
            </div>

            {questionMediaUrl && (
              <div className="mb-5 flex justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 py-4">
                <img src={questionMediaUrl} alt="Soru görseli" className="max-h-[420px] max-w-full object-contain" />
              </div>
            )}

            {(questionText || !questionMediaUrl) && (
              <div className={`rounded-2xl px-4 py-4 ${hasQuestionBody ? "bg-white" : "border border-amber-200 bg-amber-50"}`}>
                {questionText ? (
                  <div className="whitespace-pre-wrap text-lg font-semibold leading-8 text-gray-950">
                    {questionText}
                  </div>
                ) : (
                  <div className="text-sm font-semibold leading-6 text-amber-900">
                    Bu sorunun metni sistemden eksik geldi. Bu soru yerine metni veya görseli olan bir pekiştirme sorusu gösterilmeli.
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 grid gap-3">
              {options.map((option) => {
                const isSelected = selectedAnswer === option.label;
                const isCorrect = isReviewMode && question?.correctAnswer === option.label;
                const isWrongSelected = isReviewMode && isSelected && !isCorrect;
                return (
                  <button
                    key={option.label}
                    onClick={() => {
                      if (isReviewMode) return;
                      setSelectedAnswers((prev) => ({ ...prev, [questionKey]: option.label }));
                    }}
                    disabled={!hasQuestionBody && !isReviewMode}
                    className={`flex items-start gap-3 rounded-2xl border px-4 py-3.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${isCorrect
                      ? "border-emerald-400 bg-emerald-50 text-emerald-900"
                      : isWrongSelected
                        ? "border-red-400 bg-red-50 text-red-900"
                        : isSelected
                          ? "border-orange-400 bg-orange-50 text-orange-900"
                          : "border-gray-200 bg-white text-gray-800 hover:border-orange-200 hover:bg-orange-50/50"
                      }`}
                  >
                    <span className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl text-sm font-bold ${isCorrect
                      ? "bg-emerald-600 text-white"
                      : isWrongSelected
                        ? "bg-red-600 text-white"
                        : isSelected
                          ? "bg-orange-500 text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}>
                      {option.label}
                    </span>
                    <span className="pt-1 leading-6">{option.text}</span>
                  </button>
                );
              })}
            </div>

            {/* Çözümlü video — yalnız inceleme modunda, videosu olan soruda (konu anlatımından sonra öğrenci açar) */}
            {isReviewMode && question?.solutionVideoUrl && (
              <div className="mt-5 border-t border-gray-100 pt-4">
                {solutionUrl ? (
                  <div>
                    <SolutionVideoEmbed url={solutionUrl} />
                    <button
                      onClick={() => setSolutionUrl(null)}
                      className="mt-2 text-xs font-medium text-gray-500 hover:text-gray-700"
                    >
                      Videoyu kapat
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setSolutionUrl(resolveTutorMediaUrl(question.solutionVideoUrl as string))}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700 transition-colors hover:bg-violet-100"
                  >
                    🎬 Bu sorunun çözümlü videosunu izle
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                if (isReviewMode) {
                  const previousWrongIndex = reviewQuestionIndexes[Math.max(0, currentReviewPosition - 1)];
                  if (previousWrongIndex !== undefined) setCurrentIndex(previousWrongIndex);
                  setSolutionUrl(null);
                  return;
                }

                setCurrentIndex((index) => Math.max(0, index - 1));
              }}
              disabled={isReviewMode ? isReviewFirstQuestion : currentIndex === 0}
              className="rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-40"
            >
              {isReviewMode ? "Önceki Yanlış" : "Önceki"}
            </button>
            <button
              onClick={() => {
                if (isReviewMode) {
                  const nextWrongIndex = reviewQuestionIndexes[Math.min(reviewQuestionIndexes.length - 1, currentReviewPosition + 1)];
                  if (nextWrongIndex !== undefined) setCurrentIndex(nextWrongIndex);
                  setSolutionUrl(null);
                  return;
                }
                if (isLastQuestion) {
                  completeTest();
                  return;
                }
                setCurrentIndex((index) => Math.min(questions.length - 1, index + 1));
              }}
              disabled={isReviewLastQuestion || (!isReviewMode && !hasQuestionBody)}
              className="rounded-2xl bg-orange-500 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-orange-600 disabled:opacity-40"
            >
              {isReviewMode ? "Sonraki Yanlış" : isLastQuestion ? "Bitir" : "Sonraki"}
            </button>
          </div>

          {/* Yanlışları tekrar çöz — yalnız yanlış alt-kümesiyle yeni tur (körü körüne aynı testi tekrar açmaz) */}
          {isReviewMode && reviewQuestionIndexes.length > 0 && (
            <button
              onClick={() => {
                const wrongObjs = reviewQuestionIndexes.map((i) => questions[i]).filter(Boolean);
                if (!wrongObjs.length) return;
                setRetryQuestions(wrongObjs);
                setSelectedAnswers({});
                setIsReviewMode(false);
                setIsCompleted(false);
                setCurrentIndex(0);
                setSolutionUrl(null);
              }}
              className="w-full rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 shadow-sm transition hover:bg-blue-100"
            >
              Yanlışları tekrar çöz ({reviewQuestionIndexes.length} soru)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const TutorDirectVideoPlayer = ({ src, startSeconds }: { src: string; startSeconds: number }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoError, setVideoError] = useState("");

  useEffect(() => {
    setVideoError("");
  }, [src]);

  return (
    <>
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full bg-black"
        src={src}
        controls
        autoPlay
        preload="metadata"
        onLoadedMetadata={(event) => {
          if (startSeconds > 0 && Number.isFinite(event.currentTarget.duration)) {
            event.currentTarget.currentTime = Math.min(startSeconds, Math.max(0, event.currentTarget.duration - 1));
          }
        }}
        onError={() => {
          const code = videoRef.current?.error?.code;
          setVideoError(code ? `Video dosyası oynatılamadı. Hata kodu: ${code}` : "Video dosyası oynatılamadı.");
        }}
      />
      {videoError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 px-6 text-center text-sm font-semibold text-white">
          {videoError}
        </div>
      )}
    </>
  );
};

const PdfMaterialWorkspace = ({
  material,
  context,
  onMaterialAction,
}: {
  material: Extract<ActiveMaterial, { type: "pdf" }>;
  context: Record<string, any>;
  onMaterialAction: (message: string) => void;
}) => {
  const initialPageNumber = Math.max(1, Number(material.pageNumber) || 1);
  const [currentPage, setCurrentPage] = useState(initialPageNumber);
  const targetPageRef = useRef(initialPageNumber);
  const relatedPdf = context.relatedMaterials?.pdfs?.find((pdf: any) => pdf?.url === material.pdfUrl);
  const rawPageImages: any[] = (Array.isArray(material.pageImages) && material.pageImages.length
    ? material.pageImages
    : Array.isArray(relatedPdf?.pageImages)
      ? relatedPdf.pageImages
      : []);
  const pageImages: PdfPageImage[] = rawPageImages
    .map((page: any, index: number) => ({
      id: page?.id || page?.pageNumber || index + 1,
      pageNumber: Number(page?.pageNumber) || index + 1,
      imageUrl: String(page?.imageUrl || ""),
    }))
    .filter((page: PdfPageImage) => page.imageUrl)
    .sort((left: PdfPageImage, right: PdfPageImage) => left.pageNumber - right.pageNumber);
  const totalPages = Math.max(0, Number(material.totalPages || relatedPdf?.totalPages || pageImages.length || 0));
  const pdfTitle = material.title || relatedPdf?.title || context.contentTitle || "PDF Materyali";
  const resolvedPdfUrl = resolveTutorMediaUrl(material.pdfUrl);
  const pdfViewerUrl = resolvedPdfUrl
    ? `${resolvedPdfUrl}#page=${currentPage}&toolbar=0&navpanes=0&scrollbar=1&view=FitH`
    : "";
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const updateVisiblePage = () => {
    if (!scrollContainerRef.current || !pageImages.length) return;

    const container = scrollContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    let nextPage = pageImages[0]?.pageNumber || 1;
    let maxVisibleHeight = 0;

    pageImages.forEach((page, index) => {
      const node = pageRefs.current[page.pageNumber];
      if (!node) return;
      const pageNumber = Number(page.pageNumber) || index + 1;
      const pageRect = node.getBoundingClientRect();
      const visibleTop = Math.max(pageRect.top, containerRect.top);
      const visibleBottom = Math.min(pageRect.bottom, containerRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      if (visibleHeight > maxVisibleHeight) {
        maxVisibleHeight = visibleHeight;
        nextPage = pageNumber;
      }
    });

    setCurrentPage((previousPage) => (nextPage === previousPage ? previousPage : nextPage));
  };

  const scrollToPage = (nextPage: number, behavior: ScrollBehavior = "smooth") => {
    const safePage = totalPages ? Math.min(Math.max(1, nextPage), totalPages) : Math.max(1, nextPage);
    targetPageRef.current = safePage;
    const container = scrollContainerRef.current;
    const targetNode = pageRefs.current[safePage];

    if (container && targetNode) {
      setCurrentPage(safePage);
      const containerRect = container.getBoundingClientRect();
      const targetRect = targetNode.getBoundingClientRect();
      container.scrollTo({
        top: container.scrollTop + targetRect.top - containerRect.top - 8,
        behavior,
      });
      window.setTimeout(updateVisiblePage, behavior === "smooth" ? 260 : 0);
      return;
    }

    setCurrentPage(safePage);
  };

  useEffect(() => {
    targetPageRef.current = initialPageNumber;
    setCurrentPage(pageImages.length ? pageImages[0]?.pageNumber || 1 : initialPageNumber);
  }, [initialPageNumber, material.pdfUrl, pageImages.length]);

  useEffect(() => {
    if (!pageImages.length) return;

    const timers = [0, 120, 360].map((delay) =>
      window.setTimeout(() => {
        scrollToPage(initialPageNumber, delay === 0 ? "auto" : "smooth");
        updateVisiblePage();
      }, delay)
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [initialPageNumber, material.pdfUrl, pageImages.length]);

  const handleImagePdfScroll = () => {
    updateVisiblePage();
  };

  const goToPage = (nextPage: number) => {
    scrollToPage(nextPage);
  };

  return (
    <div className="h-full min-h-0 bg-white flex flex-col">
      <div className="h-12 px-4 border-b border-[#dbe7ed] bg-white flex items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-2 text-sm font-bold text-[#214b5d]">
          <FileText className="w-4 h-4 text-[#2d7087] flex-shrink-0" />
          <span className="truncate">{pdfTitle}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="min-w-[86px] rounded-full border border-[#d6e2e8] bg-[#edf6fa] px-3 py-1 text-center text-xs font-bold text-[#2d7087]">
            {totalPages ? `${currentPage}/${totalPages} sayfa` : `${currentPage}. sayfa`}
          </span>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            className="h-8 rounded-lg border border-[#d6e2e8] bg-white px-3 text-xs font-bold text-[#315869] disabled:opacity-40"
          >
            Önceki
          </button>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={!totalPages || currentPage >= totalPages}
            className="h-8 rounded-lg border border-[#d6e2e8] bg-white px-3 text-xs font-bold text-[#315869] disabled:opacity-40"
          >
            Sonraki
          </button>
          <button
            type="button"
            onClick={() => onMaterialAction(`PDF materyalini okudum. Aynı PDF'i tekrar açma; özellikle açılan ${currentPage}. sayfayı eksiğimle ilişkilendir ve sıradaki uygun adımı öner.`)}
            className="h-8 rounded-lg bg-[#2d7087] px-3 text-xs font-bold text-white transition hover:bg-[#23576a]"
          >
            PDF'i okudum
          </button>
        </div>
      </div>

      {material.pdfUrl ? (
        <div ref={scrollContainerRef} onScroll={handleImagePdfScroll} className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
          <div className="mx-auto flex max-w-[980px] flex-col gap-5">
            {pageImages.length ? (
              pageImages.map((page) => (
                <div
                  key={String(page.id || page.pageNumber)}
                  ref={(node) => {
                    pageRefs.current[page.pageNumber] = node;
                  }}
                  className="overflow-hidden rounded-[18px] border border-[#dde7eb] bg-white shadow-[0_10px_28px_rgba(26,72,92,0.08)]"
                >
                  <img
                    src={page.imageUrl}
                    alt={`${pdfTitle} sayfa ${page.pageNumber}`}
                    className="block h-auto w-full"
                    loading={page.pageNumber <= 2 ? "eager" : "lazy"}
                    onLoad={() => {
                      if (page.pageNumber !== targetPageRef.current) return;
                      window.setTimeout(() => scrollToPage(targetPageRef.current, "auto"), 0);
                    }}
                  />
                </div>
              ))
            ) : (
              <div className="overflow-hidden rounded-[18px] border border-[#dde7eb] bg-white shadow-[0_10px_28px_rgba(26,72,92,0.08)]">
                <iframe
                  key={`${resolvedPdfUrl}-${currentPage}`}
                  src={pdfViewerUrl}
                  title={`${pdfTitle} sayfa ${currentPage}`}
                  className="block h-[calc(100vh-116px)] min-h-[640px] w-full bg-white"
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <BlankWorkspace title="PDF bulunamadı" text="Bu konu için bağlı PDF URL bilgisi yok. Ders materyali bağlandığında burada açılacak." />
      )}
    </div>
  );
};

const BlankWorkspace = ({ title, text }: { title: string; text: string }) => (
  <div className="h-full bg-white flex items-center justify-center p-8">
    <div className="max-w-xl text-center">
      <div className="w-14 h-14 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center mx-auto mb-5">
        <Bot className="w-7 h-7" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-3">{title}</h2>
      <p className="text-gray-500 leading-7">{text}</p>
    </div>
  </div>
);

export default AITutor;
